/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// desk-doc.js — the in-memory desk document: the boards on the desk, the
// components seated on them (chips now; discrete parts in Feature 60), and —
// from Feature 50 — wires. Pure model, DOM-free; the renderer holds one
// instance, mutates it through these methods, and the serialized form travels
// as one desktop of the open PROJECT — the single file every desk is saved in
// (model/project-doc.js, window.chiphippo.project.save).
//
// A "board" is one STRIP — a pin-board or a power rail (board-types.js). A
// breadboard is a KIT of strips placed together in one action and joined by a
// shared `group` id (`g<n>`, or null for a loose strip). Strips in a group
// drag as one rigid unit, the way a real board's snapped-together halves do.
//
// Board x/y are strip-origin world coordinates in PITCH UNITS, snapped to
// integers so every hole lands on the global 0.1-in lattice (holes are
// integer offsets within a strip — see board-types.js). Board ids are
// `bb<n>` and component ids `c<n>`, from per-document counters that never
// reuse an id, even across delete + save + reload (`nextBoardId` /
// `nextComponentId` persist in the document).
//
// Components are `{ id, kind, ref, board, anchor, params }` — kind "chip"
// now ("discrete"/"psu" later), `ref` a catalog id, `anchor` pin 1's seated
// hole (row e). Pin positions are always DERIVED (footprints + occupancy),
// never stored; occupancy.js is the single collision authority.

import { dropRunLatches } from "../catalog/run-latches.js";
import { PROBES, restingProbe } from "../catalog/meter.js";
import {
  lyingAt,
  meterSpotsBeside,
  meters,
  pointWorld,
  probesOn,
} from "./meter-probes.js";
import { isBoardType, kitFor } from "./board-types.js";
import { WIRE_COLORS } from "./wire-colors.js";
import {
  MAX_SIGNALS,
  SIGNAL_COLORS,
  nextFlagRotation,
  nextSignalColor,
  normalizeFlagRotation,
  normalizeSignalFields,
} from "./signals.js";
import {
  DATA_LEVELS,
  ELEMENT_KINDS,
  ID_COUNTER,
  ID_PREFIX,
  MAX_DATA,
  MAX_ELEMENTS,
  TRIGGER_KEY,
  elementKindOf,
  elementSeq,
  elementsFull,
  freeDataColors,
  hasTagKey,
  isAutoTrigger,
  isPinKey,
  nextDataColor,
  nextTagRotation,
  normalizeElementFields,
  normalizeFields,
  normalizeTagRotation,
  presetFields,
  tagKeys,
} from "./integration.js";
import {
  boardSize,
  canRotate,
  formatAddress,
  holePhaseX,
  holePosition,
  isCuttable,
  normalizeCuts,
  normalizeRotation,
  parseAddress,
  parseHole,
  ROTATIONS,
  rotateOffset,
  spec,
} from "./breadboard.js";
import { footprintOffsets, partDef } from "../catalog/index.js";
import {
  boardRect as outlineRect,
  FLUSH_EPS,
  matingEdge,
  snapCorrection,
} from "./mating.js";
import { snapDesign } from "./design-clip.js";
import {
  clusterMembers,
  flagsRidingCluster,
  leadShift,
  partsRidingCluster,
  planClusterRiders,
  resolveClusterTargets,
  wiresRidingCluster,
} from "./cluster-move.js";
import {
  boardsAfterShift,
  flagsRidingPart,
  partsRidingPart,
  planPartMove,
  wiresRidingPart,
} from "./part-move.js";
import { legSeat, legsBent } from "./free-legs.js";
import {
  addressAtWorld,
  buildOccupancy,
  canCornerOffset,
  canMoveWire,
  canPlaceFlag,
  canPlacePart,
  canPlaceTag,
  canPlaceWire,
  canReendWire,
  isFreeHole,
  isRealPoint,
  partCoverAddresses,
  partPinAddresses,
  partPinHoles,
  worldOfAddress,
} from "./occupancy.js";
import { withSeat } from "./footprints.js";
import { partPinsWorld } from "./part-geometry.js";

/**
 * The schema version stamped on every document this side writes.
 *
 * It MUST equal main's `DESK_DOC_VERSION` (app/store/migrations.js), and
 * `app/tests/migrations.test.js` holds the two together. `normalizeDocument`
 * rebuilds from `emptyDocument()` and never carries a loaded document's own
 * version forward, so this number is what EVERY saved desk claims — which is
 * what lets a migration tell a document written before its change from one
 * written after. Left behind at 6 while the chain reached 11, it silently
 * meant the opposite: every desk the app wrote re-entered the chain five
 * steps back, so a migration keyed on a version could never fire once and
 * once only.
 */
export const DOC_VERSION = 15;

// The jumper-wire palette moved to model/wire-colors.js (Feature 370) so
// model/signals.js can read it without an import cycle back through here.
// Re-exported so `import { WIRE_COLORS } from "./desk-doc.js"` still resolves.
export { WIRE_COLORS };

/**
 * How a wire gets from one end to the other — its "Layout Method" (the wire's
 * own Properties dialog; the app-wide default for a NEW wire is Settings ▸
 * Appearance ▸ "Wire layout").
 *
 * · `"direct"` — the sagging bezier every wire has always been: hole to hole,
 *   nothing to decide, which is why it is the default and why the AI builder
 *   emits nothing else (a compiler places holes, not hand-drawn routes).
 * · `"routed"` — a straight run the user BENDS by dragging waypoints into it,
 *   so a wire can be taken around a board instead of over it.
 *
 * Stored omit-when-default (a direct wire carries no `layout` and no `points`),
 * the same convention as a wire's Name/Description — so a document that never
 * routed anything round-trips to the shape it had before this existed.
 */
export const WIRE_LAYOUTS = Object.freeze(["direct", "routed"]);

/** The most waypoints ONE routed wire may carry. A bend is a hand gesture, and
    twenty of them is already far past what any real jumper needs — the cap is
    what keeps a stuck drag from filling a document with them. */
export const MAX_WIRE_POINTS = 20;

/** The bus-width presets the bus tool's toolbar/keyboard shortcuts pick from —
    `name` is the grammar the tool parses (see parseBusName); 8-bit is the
    default. Listed narrowest-first, which is NOT the order the digit keys walk
    (see busWidthForKey). */
export const BUS_WIDTHS = Object.freeze([
  Object.freeze({ bits: 2, name: "D[1:0]" }),
  Object.freeze({ bits: 3, name: "D[2:0]" }),
  Object.freeze({ bits: 4, name: "D[3:0]" }),
  Object.freeze({ bits: 5, name: "D[4:0]" }),
  Object.freeze({ bits: 6, name: "D[5:0]" }),
  Object.freeze({ bits: 7, name: "D[6:0]" }),
  Object.freeze({ bits: 8, name: "D[7:0]" }),
  Object.freeze({ bits: 16, name: "D[15:0]" }),
]);

/**
 * The preset the digit keys 1–8 pick while the bus tool is armed. Every digit
 * but one NAMES ITS OWN WIDTH — `4` lays a 4-bit run — which is the whole
 * reason the mapping isn't the list's index order: a key you have to count to
 * is a key you have to look up. `1` is the 16-bit bus, because no single digit
 * can spell 16 and the widest bus is the one worth the first key.
 *
 * @returns {{bits:number, name:string}|null} null for a digit with no preset.
 */
export function busWidthForKey(digit) {
  const bits = digit === 1 ? 16 : digit;
  return BUS_WIDTHS.find((w) => w.bits === bits) ?? null;
}

/**
 * The common net names offered as quick-picks when naming a net (Feature 120).
 * They carry NO special power — the engine still derives power from PSU volts
 * and rail polarity; a name is documentation. Any string is legal.
 */
export const RESERVED_NET_NAMES = Object.freeze(["VCC", "GND", "CLK"]);

/** Annotation kinds (Feature 120): a one-line label vs a multi-line note. */
const ANNOTATION_KINDS = new Set(["label", "note"]);

/** The widest bus the name grammar will mint — a guard against a `D[0:9999]`
    typo trying to lay ten thousand wires. */
export const MAX_BUS_WIDTH = 64;

const BUS_NAME_RE = /^(.*?)\[\s*(\d+)\s*:\s*(\d+)\s*\]$/;

/**
 * Parse a bus name into its width + bit order (Feature 130). The grammar is a
 * base plus an optional `[hi:lo]` (msb:lsb, e.g. `D[7:0]`) or `[lo:hi]`
 * (`A[0:15]`); a bare name is a width-1 "bus" (a named single wire). The `bits`
 * array is the bit NUMBER each ordered member carries — `D[7:0]` → [7,6,…,0],
 * so member 0 is the msb and a pin-tap wires it to the high pin.
 *
 * @returns {{ base:string, width:number, hi:number, lo:number,
 *   order:"asc"|"desc"|"single", bits:number[] }|null} null for junk or a
 *   width past MAX_BUS_WIDTH.
 */
export function parseBusName(name) {
  if (typeof name !== "string") return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const m = BUS_NAME_RE.exec(trimmed);
  if (!m) {
    return {
      base: trimmed,
      width: 1,
      hi: 0,
      lo: 0,
      order: "single",
      bits: [0],
    };
  }
  const a = Number(m[2]);
  const b = Number(m[3]);
  const width = Math.abs(a - b) + 1;
  if (width > MAX_BUS_WIDTH) return null;
  const step = a <= b ? 1 : -1;
  const bits = [];
  for (let i = 0; i < width; i += 1) bits.push(a + i * step);
  return {
    base: m[1].trim(),
    width,
    hi: Math.max(a, b),
    lo: Math.min(a, b),
    order: a <= b ? "asc" : "desc",
    bits,
  };
}

const BOARD_ID_RE = /^bb([1-9]\d*)$/;
const GROUP_ID_RE = /^g([1-9]\d*)$/;
const COMPONENT_ID_RE = /^c([1-9]\d*)$/;
const PSU_ID_RE = /^psu([1-9]\d*)$/;
const CLOCK_ID_RE = /^clk([1-9]\d*)$/;
const LOAD_ID_RE = /^load([1-9]\d*)$/;
const AC_ID_RE = /^ac([1-9]\d*)$/;
const TRANSFORMER_ID_RE = /^xfmr([1-9]\d*)$/;
const ADAPTER_ID_RE = /^adp([1-9]\d*)$/;
const METER_ID_RE = /^dmm([1-9]\d*)$/;
const WIRE_ID_RE = /^w([1-9]\d*)$/;
const ANNOTATION_ID_RE = /^an([1-9]\d*)$/;
const BUS_ID_RE = /^bus([1-9]\d*)$/;
const SCOPE_CHANNEL_ID_RE = /^sc([1-9]\d*)$/;
const SIGNAL_ID_RE = /^sig([1-9]\d*)$/;

/** Logic-analyzer channel kinds (Feature 210): a single net or a whole bus. */
const SCOPE_CHANNEL_KINDS = new Set(["net", "bus"]);

/** Desk-level bricks (no board): kind → { id regex, id prefix, next counter }. */
const BRICKS = Object.freeze({
  psu: { re: PSU_ID_RE, prefix: "psu", counter: "nextPsuId" },
  clock: { re: CLOCK_ID_RE, prefix: "clk", counter: "nextClockId" },
  // The electronic load (catalog/bench-parts.js). Its counter is stored only
  // once one has been placed, so a desk without one keeps its bytes.
  load: { re: LOAD_ID_RE, prefix: "load", counter: "nextLoadId", optional: true }, // prettier-ignore
  // The AC bricks (catalog/ac-parts.js), their counters stored the same way.
  ac: { re: AC_ID_RE, prefix: "ac", counter: "nextAcId", optional: true },
  transformer: { re: TRANSFORMER_ID_RE, prefix: "xfmr", counter: "nextTransformerId", optional: true }, // prettier-ignore
  adapter: { re: ADAPTER_ID_RE, prefix: "adp", counter: "nextAdapterId", optional: true }, // prettier-ignore
  // The multimeter (catalog/meter.js), its counter stored the same way.
  meter: { re: METER_ID_RE, prefix: "dmm", counter: "nextMeterId", optional: true }, // prettier-ignore
});

/** Params coerced through the def's own contract (chips have none). */
function normalizeParams(def, raw) {
  return def.normalizeParams ? def.normalizeParams(raw) : {};
}

/**
 * Params coerced on the way IN from a stored document, which is the same thing
 * MINUS the run state.
 *
 * `damaged` is a chip's 12 V kill, and it is in the document only because the
 * pure engine reads the document — it is the latch that keeps a chip dead for
 * the rest of the run (see `SimController#persistDamage`). It is not a property
 * of the CIRCUIT, and a stop clears it, so a document must never load one in:
 * that would be a dead chip nothing could revive short of deleting it. Two ways
 * one can be in a file at all — a project saved mid-run, and any document
 * written before damage became run-volatile — and this covers both, along with
 * every import and paste, since everything reaches the desk through here.
 *
 * Deliberately NOT folded into `normalizeParams` itself: `setComponentParams`
 * shares that path, and the latch has to be able to get in while running.
 */
function loadParams(def, raw) {
  // 12 V's magic smoke and Spice Lite's brown smoke (catalog/run-latches.js).
  return dropRunLatches(normalizeParams(def, raw));
}

/**
 * A component's optional `schematicPos` nudge (Feature 150): a finite `{x,y}`
 * or undefined. Purely a layout hint for the derived schematic view — the desk
 * placement is unaffected.
 */
function normalizeSchematicPos(raw) {
  if (!raw || typeof raw !== "object") return undefined;
  const { x, y } = raw;
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined;
}

/**
 * Apply an optional Name/Description pair (the shared Properties dialog) to a
 * board or component record, present only when set to a non-empty string —
 * same omit-when-empty convention as schematicPos, so a record that never had
 * one round-trips to the identical shape it had before these fields existed.
 */
/** A deep-enough copy of an integration element: its fields, its tags and a
    parked trigger are the nested things a caller could otherwise mutate
    through. */
function copyElement(e) {
  return {
    ...e,
    fields: e.fields.map((f) => ({ ...f })),
    ...(e.tags
      ? {
          tags: Object.fromEntries(
            Object.entries(e.tags).map(([k, v]) => [k, { ...v }]),
          ),
        }
      : {}),
    ...(e.parkedTrigger ? { parkedTrigger: { ...e.parkedTrigger } } : {}),
    ...(e.drive ? { drive: [...e.drive] } : {}),
  };
}

/** A data bus's pin left the board: it drives nothing until planted again
    and set. Anything else — an Output's or Input's tag — is untouched. */
function releaseDataPin(element, key) {
  const i = Number(key) - 1;
  if (element.drive && i >= 0 && i < element.drive.length) {
    element.drive[i] = "z";
  }
}

function applyMeta(record, raw) {
  if (typeof raw?.name === "string" && raw.name) record.name = raw.name;
  if (typeof raw?.description === "string" && raw.description) {
    record.description = raw.description;
  }
}

function taggedError(message, code) {
  const err = new Error(message);
  err.code = code;
  return err;
}

/** Move the record `id` to place `index` in `list`, in place — the rail's
    reorder, for signals and elements alike. Returns whether it moved. */
function moveWithin(list, id, index, what) {
  const from = list.findIndex((x) => x.id === id);
  if (from === -1) throw taggedError(`no ${what} ${id}`, "NOT_FOUND");
  if (!Number.isInteger(index) || index < 0 || index >= list.length) {
    throw taggedError(`bad ${what} place: ${index}`, "INVALID_ARG");
  }
  if (index === from) return false;
  const [record] = list.splice(from, 1);
  list.splice(index, 0, record);
  return true;
}

/** A routed wire's waypoint, in world pitch units. Unlike everything else on
    the desk a waypoint is NOT on the lattice — it is a free point in the space
    between the boards, so it keeps two decimals rather than rounding to a hole
    it has nothing to do with. */
function wireCoord(n) {
  return Math.round(n * 100) / 100;
}

/**
 * A board coordinate on the grid the document STORES boards on: two decimals,
 * the same quantum a wire waypoint keeps.
 *
 * It used to be whole pitches, which was right while every strip was a whole
 * number of them tall. It is not any more: board-types.js measures a rail at
 * 3.50 pitches and a pin-board at 14.02, so the strips of an ordinary 830 kit
 * sit at y 0, 3.50 and 17.52 — and `Math.round` here would jam each of them
 * back into the one above it. Every vertical dimension in board-types.js is an
 * exact multiple of 0.01, so a stack lands on this grid exactly and stays
 * flush through a save and a reload.
 *
 * WHAT KEEPS THE COLUMNS LINED UP is not this function but the two things that
 * feed it: a board is PLACED at a whole-pitch x (`placeX`), and a drag moves it
 * by a whole-pitch delta. The only thing that ever puts a board on a fractional
 * x is a dovetail against something whose width is fractional — an upright rail
 * is 3.50 wide — which is precisely a case where the exact value is the point.
 */
function boardCoord(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Each pin of a part as the vector from pin 1's HOLE to its own — the
 * holes they resolve to, not the offsets stored — or null when one of them
 * resolves to nothing. A turned lead lands ON the hole it reaches
 * (lead-offset.js): a vector only NEAR one (2.76 turned on its side is 0.24
 * short of a column) would be drawn beside the hole it is electrically in.
 */
function pinVectors(doc, comp) {
  const pins = partPinAddresses(doc, comp);
  if (!pins?.length || pins.some((p) => p.address == null)) return null;
  const at = pins.map((p) => worldOfAddress(doc.boards, p.address));
  if (at.some((w) => !w)) return null;
  return at.map((w) => ({ dx: w.x - at[0].x, dy: w.y - at[0].y }));
}

/** Where a board is PLACED: x snaps to the integer column lattice, so a strip
    dropped anywhere on the desk lines its columns up with every other. */
function placeX(n, type = null) {
  // A veroboard's holes sit at a fraction inside its edge (its margins are
  // even, board-types.js), so it is the HOLES that go on the column lattice
  // and the board's own x that takes the fraction.
  const phase = type ? holePhaseX(type) : 0;
  return phase ? boardCoord(Math.round(n + phase) - phase) : Math.round(n);
}

/** A magnetic pull, on the same grid. `snapCorrection` is pure rect geometry
    and works in whatever the caller hands it, so the difference of two measured
    strip heights comes back as 2.0000000000000036 — true enough to snap with,
    but not something to store or to state. Quantizing here lands the drop
    exactly flush and keeps the reported pull readable. */
function quantizePull({ dx, dy }) {
  return { dx: boardCoord(dx), dy: boardCoord(dy) };
}

/** Coerce a raw waypoint list: real finite points only, capped at
    MAX_WIRE_POINTS. Junk is dropped rather than refused — a hand-edited or
    foreign document should lose a bad bend, not its wire. */
function normalizeWirePoints(raw) {
  if (!Array.isArray(raw)) return [];
  const points = [];
  for (const p of raw) {
    if (!p || typeof p !== "object") continue;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    points.push({ x: wireCoord(p.x), y: wireCoord(p.y) });
    if (points.length === MAX_WIRE_POINTS) break;
  }
  return points;
}

/** Apply a wire's layout to a record, omit-when-default: a direct wire carries
    neither field, so it stays byte-identical to a pre-routing document. */
function applyWireLayout(record, layout, points) {
  if (layout !== "routed") return record;
  record.layout = "routed";
  const list = normalizeWirePoints(points);
  if (list.length > 0) record.points = list;
  return record;
}

/** A defensive copy of a wire — its waypoints are copied too, so a caller can
    never reach back into the document through the array it was handed. */
function copyWire(wire) {
  const copy = { ...wire };
  if (wire.points) copy.points = wire.points.map((p) => ({ ...p }));
  return copy;
}

/** A fresh, empty desk document. */
export function emptyDocument() {
  return {
    version: DOC_VERSION,
    boards: [],
    components: [],
    wires: [],
    buses: [],
    netNames: [],
    annotations: [],
    scopeChannels: [],
    signals: [],
    integrations: [],
    nextBoardId: 1,
    nextGroupId: 1,
    nextComponentId: 1,
    nextPsuId: 1,
    nextClockId: 1,
    nextWireId: 1,
    nextBusId: 1,
    nextAnnotationId: 1,
    nextScopeChannelId: 1,
    nextSignalId: 1,
    nextOutputId: 1,
    nextInputId: 1,
    nextDataId: 1,
  };
}

/** Every list a desk's CONTENT lives in, read off the empty document itself so
    a list added later can never be forgotten here. */
const CONTENT_KEYS = Object.freeze(
  Object.entries(emptyDocument())
    .filter(([, value]) => Array.isArray(value))
    .map(([key]) => key),
);

/**
 * Is there NOTHING on this desk? No boards, parts, wires, buses, net names,
 * labels, or scope channels — so there is nothing in it to keep or to lose.
 *
 * The `next*Id` counters are deliberately not read: they say what a desk has
 * ever HELD, not what it holds. A board placed and then deleted leaves the
 * desk as empty as it started, and this must say so.
 */
export function isEmptyDocument(doc) {
  if (!doc || typeof doc !== "object") return true;
  return CONTENT_KEYS.every((key) => (doc[key]?.length ?? 0) === 0);
}

/**
 * Coerce a loaded (possibly junk/foreign) document into a valid one: arrays
 * forced; board/component entries with bad ids, types/refs, coords, or
 * dangling board references dropped; coordinates snapped to integers; and
 * the id counters advanced past every surviving id. Wires are carried
 * through verbatim (Feature 50 normalizes them).
 */
export function normalizeDocument(raw) {
  const doc = emptyDocument();
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return doc;

  // The board outlines accepted so far — see the OVERLAP note below.
  const taken = [];

  let maxBoardSeq = 0;
  let maxGroupSeq = 0;
  const boardIds = new Set();
  const boards = Array.isArray(raw.boards) ? raw.boards : [];
  for (const b of boards) {
    if (!b || typeof b !== "object") continue;
    const m = typeof b.id === "string" ? BOARD_ID_RE.exec(b.id) : null;
    if (!m || boardIds.has(b.id)) continue;
    if (!isBoardType(b.type)) continue;
    if (!Number.isFinite(b.x) || !Number.isFinite(b.y)) continue;
    // A junk group id degrades to a loose strip rather than dropping it.
    const g = typeof b.group === "string" ? GROUP_ID_RE.exec(b.group) : null;
    const board = {
      id: b.id,
      type: b.type,
      x: boardCoord(b.x),
      y: boardCoord(b.y),
      rot: normalizeRotation(b.type, b.rot),
      group: g ? b.group : null,
    };
    // A veroboard's cut tracks: real holes of it only, each once, in one
    // order — and the key only when there are any (omit-when-default).
    const cuts = normalizeCuts(b.type, b.cuts);
    if (cuts.length > 0) board.cuts = cuts;
    // OVERLAP IS AS LOADABLE AS ANY OTHER NONSENSE, so it is checked here like
    // the rest. `canPlace` refuses it at placement time and `canMoveBoardsBy`
    // at drop time, but neither runs on the way in — so a hand-edited or
    // corrupt file could seat two strips through each other, and then NEITHER
    // could ever be moved: every drag re-checks against the other and is
    // refused, with no cue but a drop that will not take. Exactly the shape of
    // the seated-component check below ("a DIP anchored past the end of a strip
    // used to load clean … while the chip sat there electrically dead") — a
    // desk you cannot rearrange is the same bug wearing geometry.
    //
    // First-wins, like every other dedupe here. Dropping the LATER strip also
    // cascades correctly for free: its seated parts fail `boardIds.has`, and
    // its wires fail `validEndpoint`.
    const rect = outlineRect(board);
    if (taken.some((r) => rectsOverlap(rect, r))) continue;
    taken.push(rect);
    boardIds.add(b.id);
    maxBoardSeq = Math.max(maxBoardSeq, Number(m[1]));
    if (g) maxGroupSeq = Math.max(maxGroupSeq, Number(g[1]));
    applyMeta(board, b);
    doc.boards.push(board);
  }

  let maxCompSeq = 0;
  const maxBrickSeq = { psu: 0, clock: 0, load: 0, ac: 0, transformer: 0, adapter: 0, meter: 0 }; // prettier-ignore
  const compIds = new Set();
  // Every hole an accepted part's pins already claim — see the ONE HOLE, ONE
  // LEAD note below. Bricks are absent by construction: a terminal (`psu1.+`)
  // is its own address space and can never collide with a board hole.
  const claimedPoints = new Set();
  const components = Array.isArray(raw.components) ? raw.components : [];
  const unplacedMeters = [];
  for (const c of components) {
    if (!c || typeof c !== "object" || compIds.has(c.id)) continue;
    const def = partDef(c.ref);
    if (!def) continue;
    const brick = BRICKS[c.kind];
    if (brick && def.kind === c.kind) {
      const m = typeof c.id === "string" ? brick.re.exec(c.id) : null;
      if (!m) continue;
      // A meter's place is the session's, never the file's (meter-probes.js
      // `meterForFile`): one arriving without one is placed beside the
      // circuit once everything else is in (below).
      const placed = Number.isFinite(c.x) && Number.isFinite(c.y);
      if (!placed && c.kind !== "meter") continue;
      compIds.add(c.id);
      maxBrickSeq[c.kind] = Math.max(maxBrickSeq[c.kind], Number(m[1]));
      const brickRecord = {
        id: c.id,
        kind: c.kind,
        ref: c.ref,
        x: placed ? Math.round(c.x) : 0,
        // y keeps the 0.01 grid a board is stored on, not a whole pitch: a
        // recentre (`translateAll`) slides everything by a board's fractional
        // y, and rounding the brick here on the NEXT load moved it up to half a
        // pitch away from the boards it was placed beside.
        y: placed ? boardCoord(c.y) : 0,
        // (A meter's blown fuse is a run latch: never loaded in.)
        params: loadParams(def, c.params),
      };
      // A BRICK's footprint is deliberately NOT checked here, though
      // `canPlaceBrick` refuses one over a board — because the two overlaps
      // are not the same problem. Two overlapping BOARDS deadlock: every drag
      // of either re-checks against the other and is refused, so neither can
      // ever be moved again. An overlapping brick is only a nuisance — it
      // cannot be dragged over a board, but it can always be dragged off one —
      // and dropping it on load would be a silent deletion to fix a nuisance.
      // (The `65xx-lcd` demo used to be the live example, its LCD module
      // sitting squarely on a breadboard; that module is a SEATED part now,
      // so no shipped document relies on this leniency any more.)
      applyMeta(brickRecord, c);
      doc.components.push(brickRecord);
      if (!placed) unplacedMeters.push(brickRecord);
      continue;
    }
    if (c.kind !== def.kind || (c.kind !== "chip" && c.kind !== "discrete")) {
      continue;
    }
    const m = typeof c.id === "string" ? COMPONENT_ID_RE.exec(c.id) : null;
    if (!m) continue;
    if (!boardIds.has(c.board)) continue; // seated on a surviving board
    if (typeof c.anchor !== "string") continue;
    // …and actually SEATED on it: the anchor has to fit the part's footprint,
    // and every hole that footprint lands on has to exist on that board. A DIP
    // anchored past the end of a strip used to load "clean" with its
    // overhanging pins resolving to nothing — the counts matched, so even an
    // entity-count check passed, while the chip sat there electrically dead.
    // A rotated part's BENT lead is deliberately exempt: it carries an
    // {offset} rather than a hole and may legally resolve to nothing, which is
    // the documented state a part falls into when a rail moves out from under
    // it. Floating is a state you fall into, never one you load into.
    const params = loadParams(def, c.params);
    const seat = partPinHoles(c.ref, c.anchor, params);
    if (!seat) continue; // anchor doesn't fit the footprint at all
    const seatBoard = doc.boards.find((b) => b.id === c.board);
    if (
      seat.some((p) => p.hole != null && !parseHole(seatBoard.type, p.hole))
    ) {
      continue;
    }
    const record = {
      id: c.id,
      kind: c.kind,
      ref: c.ref,
      board: c.board,
      anchor: c.anchor,
      params,
    };
    // ONE HOLE, ONE LEAD — the other half of "seated", and the half the check
    // above cannot see. That one proves each pin's hole EXISTS; this proves it
    // is FREE. `addComponent` has always refused an overlap (`canPlacePart`),
    // so only a hand-edited or corrupt file can carry one — and it loaded
    // clean, with `buildOccupancy`'s last-writer-wins silently masking the
    // loser's pins ENTIRELY: two 14-pin DIPs in one set of columns produced 14
    // occupancy entries, so the hover readout, the probe and the build guide
    // all named one chip where two sat, while the netlist joined both. Exactly
    // the shape of the board OVERLAP check above, one level down.
    //
    // Resolved through `partPinAddresses` rather than the `seat` holes, so a
    // rotated part's BENT lead is checked too — it lands on whatever strip lies
    // under it, which is as real a claim on that hole as a footprint pin's. A
    // lead resolving to nothing claims nothing (floating is legal).
    //
    // First wins, like every other dedupe here. Nothing cascades: dropping the
    // loser FREES holes rather than removing any, so every wire that was legal
    // stays legal — which is why the wire loop below still re-derives occupancy
    // from the surviving parts instead of reading this set.
    //
    // A 600-mil chip at its true width claims the holes its BODY covers too
    // (rows e–g between its pins — `partCoverAddresses`): nothing may be
    // plugged in under the plastic, so a part whose lead is there, or a chip
    // whose body would land on an earlier part's lead, is the loser here.
    const claims = [];
    for (const { address } of partPinAddresses(doc, record) ?? []) {
      if (address != null) claims.push(address);
    }
    claims.push(...partCoverAddresses(doc, record));
    if (claims.some((a) => claimedPoints.has(a))) continue;
    // …and one lead per hole within the part itself: a bend ending in its
    // own anchor's hole (or under its own body) is the two-leads-in-one-hole
    // part `canPlacePart` refuses — loaded, a resistor shorted across itself.
    if (new Set(claims).size !== claims.length) continue;
    for (const a of claims) claimedPoints.add(a);
    compIds.add(c.id);
    maxCompSeq = Math.max(maxCompSeq, Number(m[1]));
    const schematicPos = normalizeSchematicPos(c.schematicPos);
    if (schematicPos) record.schematicPos = schematicPos; // Feature 150 nudge
    applyMeta(record, c);
    doc.components.push(record);
  }
  // The meters that came with no place: in a row beside the circuit.
  meterSpotsBeside(doc, unplacedMeters.length).forEach((at, i) => {
    unplacedMeters[i].x = at.x;
    unplacedMeters[i].y = at.y;
  });

  // Wires: both endpoints must parse onto surviving boards' real holes (or
  // surviving PSU terminals) and be distinct; junk colors fall back to the
  // first palette entry.
  let maxWireSeq = 0;
  const wireIds = new Set();
  const validEndpoint = (address) => {
    if (typeof address !== "string") return false;
    const parsed = parseAddress(address);
    if (!parsed) return false;
    const board = doc.boards.find((b) => b.id === parsed.boardId);
    if (board) return parseHole(board.type, parsed.hole) !== null;
    const comp = doc.components.find((c) => c.id === parsed.boardId);
    if (!comp) return false;
    const def = partDef(comp.ref);
    return Boolean(def?.terminals?.some((t) => t.id === parsed.hole));
  };
  // A meter's probe rests on a REAL point — a hole of a surviving board, a
  // terminal of a surviving brick — or it lies loose where a new meter's
  // does. It claims nothing: a probe is no lead, so it may share a hole —
  // but not one under a part's body, which pushed it off (pressProbesOff):
  // it lies where its tip was.
  let covered = null;
  for (const comp of doc.components) {
    if (comp.kind !== "meter") continue;
    for (const which of PROBES) {
      const at = comp.params[which]?.at;
      if (typeof at !== "string") continue;
      if (!validEndpoint(at)) {
        comp.params[which] = restingProbe(which);
        continue;
      }
      covered ??= new Set(
        doc.components.flatMap((c) =>
          c.kind === "chip" || c.kind === "discrete"
            ? partCoverAddresses(doc, c)
            : [],
        ),
      );
      if (covered.has(at)) {
        const tip = pointWorld(doc, at);
        comp.params[which] = tip ? lyingAt(comp, tip) : restingProbe(which);
      }
    }
  }
  // One lead per point: validEndpoint only proves an endpoint is a REAL hole/
  // terminal, not that it is FREE. Seed the claimed set with the seated parts'
  // pin holes and the holes their bodies cover (doc.wires is still empty, so
  // buildOccupancy yields parts only),
  // then claim each wire's ends as they load. A foreign/hand-edited doc with
  // two leads on one hole would otherwise have the loser silently hidden by
  // buildOccupancy's last-writer-wins.
  const claimed = new Set(buildOccupancy(doc).keys());
  const wires = Array.isArray(raw.wires) ? raw.wires : [];
  for (const w of wires) {
    if (!w || typeof w !== "object") continue;
    const m = typeof w.id === "string" ? WIRE_ID_RE.exec(w.id) : null;
    if (!m || wireIds.has(w.id)) continue;
    if (!validEndpoint(w.from) || !validEndpoint(w.to)) continue;
    if (w.from === w.to) continue;
    if (claimed.has(w.from) || claimed.has(w.to)) continue; // point already taken
    wireIds.add(w.id);
    claimed.add(w.from);
    claimed.add(w.to);
    maxWireSeq = Math.max(maxWireSeq, Number(m[1]));
    const wireRecord = {
      id: w.id,
      from: w.from,
      to: w.to,
      color: WIRE_COLORS.includes(w.color) ? w.color : WIRE_COLORS[0],
    };
    applyWireLayout(wireRecord, w.layout, w.points);
    applyMeta(wireRecord, w);
    doc.wires.push(wireRecord);
  }

  // Signals (Feature 370): bench stimulus. A signal's FLAG plugs its point
  // into one board hole, so it extends the very same `claimed` set — the
  // app has one ordering rule for a hole, stated here: PINS, then WIRES, then
  // FLAGS, first wins.
  //
  // THE RULE, stated once and reused verbatim by removeBoard and pasteDesign:
  // a flag with nowhere to go stops existing; the SIGNAL never does. A deleted
  // board, a hand-edited anchor, a hole someone else took — the flag drops and
  // the button comes back on the rail, unplaced.
  //
  // Two signals may share a colour (colours cycle once the seven are used; the
  // KEY is what tells them apart), so a duplicate is not a fault. A colour
  // that is not a signal colour at all — BLACK, which was withdrawn, or junk —
  // is REPAIRED rather than dropped (a colour is presentation, and losing a
  // whole stimulus source to a cosmetic fault is the wrong trade), and the
  // repair moves nothing else: every signal holding a valid colour keeps it,
  // and each bad one takes `nextSignalColor` over everything already kept, so
  // it lands in the gap the kept ones leave rather than shoving them along.
  //
  // The cap is the digit row (`MAX_SIGNALS`): past it, a signal is dropped.
  let maxSignalSeq = 0;
  const signalIds = new Set();
  const rawSignals = Array.isArray(raw.signals) ? raw.signals : [];

  // Pass 1 — which entries are signals at all, capped at one per key.
  const accepted = [];
  for (const sig of rawSignals) {
    if (!sig || typeof sig !== "object") continue;
    const m = typeof sig.id === "string" ? SIGNAL_ID_RE.exec(sig.id) : null;
    if (!m || signalIds.has(sig.id)) continue;
    if (accepted.length >= MAX_SIGNALS) break;
    signalIds.add(sig.id);
    maxSignalSeq = Math.max(maxSignalSeq, Number(m[1]));
    accepted.push(sig);
  }

  // Pass 2 — the records. `colored` starts as every signal that came with a
  // good colour, so a repair sees the WHOLE rail's colours (later signals'
  // included) and each repaired one joins it before the next is decided.
  const colored = accepted
    .filter((sig) => SIGNAL_COLORS.includes(sig.color))
    .map((sig) => ({ color: sig.color }));
  for (const sig of accepted) {
    const fields = normalizeSignalFields(sig, colored);
    if (fields.color !== sig.color) colored.push({ color: fields.color });
    const record = { id: sig.id, ...fields };
    applyMeta(record, sig);
    // A flag anchors to a BOARD hole only: a component terminal is a
    // connection point, not something you can plug a flag into.
    const anchor = sig.flag?.anchor;
    if (typeof anchor === "string" && !claimed.has(anchor)) {
      const parsed = parseAddress(anchor);
      const board = parsed && doc.boards.find((b) => b.id === parsed.boardId);
      if (board && parseHole(board.type, parsed.hole) !== null) {
        record.flag = { anchor, rot: normalizeFlagRotation(sig.flag?.rot) };
        claimed.add(anchor);
      }
    }
    doc.signals.push(record);
  }

  // Serial integration: Output and Input elements (model/integration.js). An
  // element's TAGS plug into board holes exactly as a signal's flag does, so
  // they extend the very same `claimed` set — PINS, then WIRES, then FLAGS,
  // then TAGS, first wins — and follow THE RULE for a homeless flag word for
  // word: a tag with nowhere to go stops existing; the ELEMENT never does. The
  // kind is read off the id (`out3`, `in1`), never trusted from a field that
  // could disagree with it, and a colour is repaired the way a signal's is.
  const maxElementSeq = { output: 0, input: 0, data: 0 };
  const elementIds = new Set();
  // Kept with their kind: a data bus's colour must be its own among the data
  // buses (`freeDataColors`), first wins.
  const colorsKept = doc.signals.map((s) => ({ color: s.color }));
  const rawElements = Array.isArray(raw.integrations) ? raw.integrations : [];
  const isHole = (anchor) => {
    const parsed = typeof anchor === "string" ? parseAddress(anchor) : null;
    const board = parsed && doc.boards.find((b) => b.id === parsed.boardId);
    return Boolean(board) && parseHole(board.type, parsed.hole) !== null;
  };
  for (const e of rawElements) {
    if (!e || typeof e !== "object") continue;
    const kind = elementKindOf(e.id);
    if (!kind || elementIds.has(e.id)) continue;
    // Each cap counts only its own kind (`elementsFull`), so a full rail of
    // Outputs/Inputs still loads its data buses, and the other way round.
    if (elementsFull(doc.integrations, kind)) continue;
    elementIds.add(e.id);
    maxElementSeq[kind] = Math.max(maxElementSeq[kind], elementSeq(e.id));
    const record = { id: e.id, kind, ...normalizeElementFields(e, colorsKept) };
    colorsKept.push({ kind, color: record.color });
    applyMeta(record, e);
    const tags = {};
    for (const key of tagKeys(record)) {
      const tag = e.tags?.[key];
      const anchor = tag?.anchor;
      if (typeof anchor !== "string" || claimed.has(anchor)) continue;
      if (!isHole(anchor)) continue;
      tags[key] = { anchor, rot: normalizeTagRotation(tag.rot) };
      claimed.add(anchor);
    }
    if (Object.keys(tags).length) record.tags = tags;
    // A data pin drives only from a hole: an unplugged one reads Z, so a tag
    // planted later never arrives already driving (`unplantIntegrationTag`).
    if (record.drive) {
      record.drive = record.drive.map((level, i) =>
        tags[String(i + 1)] ? level : "z",
      );
    }
    // An element on Auto has no trigger tag, but REMEMBERS where it was
    // (`parkedTrigger`), so switching back to an edge puts it back. A memory,
    // not a tag: it claims no hole, drives nothing and is drawn nowhere, so
    // it is held only to naming a real hole — whether that hole is still free
    // is asked when it is restored. Only Auto keeps one; an Auto element
    // arriving with a planted trigger (a state the app never writes) has it
    // parked, the reading that loses nothing.
    if (isAutoTrigger(record)) {
      const park = e.parkedTrigger ?? e.tags?.[TRIGGER_KEY];
      if (isHole(park?.anchor)) {
        record.parkedTrigger = {
          anchor: park.anchor,
          rot: normalizeTagRotation(park.rot),
        };
      }
    }
    doc.integrations.push(record);
  }

  // Buses (Feature 130): metadata over wires — `{ id, name, width, color,
  // members: [wireId…] }`. Each member must be a surviving wire; junk names
  // drop the bus. `width` is repaired up so it never undercounts its members
  // (a name change may have shrunk the declared width below what was laid).
  let maxBusSeq = 0;
  const busIds = new Set();
  const buses = Array.isArray(raw.buses) ? raw.buses : [];
  for (const bus of buses) {
    if (!bus || typeof bus !== "object") continue;
    const m = typeof bus.id === "string" ? BUS_ID_RE.exec(bus.id) : null;
    if (!m || busIds.has(bus.id)) continue;
    const parsed = parseBusName(bus.name);
    if (!parsed) continue; // an unparseable name is not a bus
    busIds.add(bus.id);
    maxBusSeq = Math.max(maxBusSeq, Number(m[1]));
    const seen = new Set();
    const members = [];
    for (const wid of Array.isArray(bus.members) ? bus.members : []) {
      if (wireIds.has(wid) && !seen.has(wid)) {
        seen.add(wid);
        members.push(wid);
      }
    }
    doc.buses.push({
      id: bus.id,
      name: bus.name.trim(),
      width: Math.max(parsed.width, members.length, 1),
      color: WIRE_COLORS.includes(bus.color) ? bus.color : WIRE_COLORS[0],
      members,
    });
  }

  // Net names (Feature 120): a binding is `{ address, name }` — the user names
  // a net by pointing at ONE member hole/terminal on it, so the name survives a
  // net-key change. Drop a binding whose address no longer parses; dedupe by
  // address (first wins). Never resolved here — that is the netlist's job.
  const namedAddresses = new Set();
  const netNames = Array.isArray(raw.netNames) ? raw.netNames : [];
  for (const b of netNames) {
    if (!b || typeof b !== "object") continue;
    if (!parseAddress(b.address)) continue;
    if (typeof b.name !== "string" || b.name.trim() === "") continue;
    if (namedAddresses.has(b.address)) continue;
    namedAddresses.add(b.address);
    doc.netNames.push({ address: b.address, name: b.name.trim() });
  }

  // Annotations (Feature 120): pure desk decoration — a `label` (one-line) or a
  // `note` (multi-line), positioned in world pitch units, ignored by occupancy,
  // the netlist, and the engine. `anchor` (a component id) makes it ride that
  // part's moves; `color` is an optional CSS color string.
  let maxAnnSeq = 0;
  const annIds = new Set();
  const annotations = Array.isArray(raw.annotations) ? raw.annotations : [];
  for (const a of annotations) {
    if (!a || typeof a !== "object") continue;
    const m = typeof a.id === "string" ? ANNOTATION_ID_RE.exec(a.id) : null;
    if (!m || annIds.has(a.id)) continue;
    if (!ANNOTATION_KINDS.has(a.kind)) continue;
    if (!Number.isFinite(a.x) || !Number.isFinite(a.y)) continue;
    annIds.add(a.id);
    maxAnnSeq = Math.max(maxAnnSeq, Number(m[1]));
    const ann = {
      id: a.id,
      kind: a.kind,
      x: a.x,
      y: a.y,
      text: typeof a.text === "string" ? a.text : "",
    };
    if (typeof a.color === "string" && a.color) ann.color = a.color;
    if (typeof a.anchor === "string" && a.anchor) ann.anchor = a.anchor;
    doc.annotations.push(ann);
  }

  // Scope channels (Feature 210): the logic-analyzer's instrument setup — an
  // ordered list of { kind:"net"|"bus", ref } bindings (ref = a member address
  // or a bus id) with an optional label/color. Additive and passive: they touch
  // neither occupancy, the netlist, nor the engine, so a stale ref simply reads
  // as undriven until its target returns.
  let maxScopeSeq = 0;
  const scopeIds = new Set();
  const scopeChannels = Array.isArray(raw.scopeChannels)
    ? raw.scopeChannels
    : [];
  for (const s of scopeChannels) {
    if (!s || typeof s !== "object") continue;
    const m = typeof s.id === "string" ? SCOPE_CHANNEL_ID_RE.exec(s.id) : null;
    if (!m || scopeIds.has(s.id)) continue;
    if (!SCOPE_CHANNEL_KINDS.has(s.kind)) continue;
    if (typeof s.ref !== "string" || !s.ref) continue;
    scopeIds.add(s.id);
    maxScopeSeq = Math.max(maxScopeSeq, Number(m[1]));
    const ch = { id: s.id, kind: s.kind, ref: s.ref };
    if (typeof s.label === "string" && s.label) ch.label = s.label;
    if (typeof s.color === "string" && s.color) ch.color = s.color;
    doc.scopeChannels.push(ch);
  }
  // The analyzer's ground lead: the member address every voltage lane is
  // read against (and, on a floating AC adapter circuit, that circuit's
  // ground — sim/floating-grounds.js). Stored only when placed; a stale one
  // reads as no lead until its target returns, as a channel's does.
  if (typeof raw.scopeGround === "string" && parseAddress(raw.scopeGround)) {
    doc.scopeGround = raw.scopeGround;
  }

  const storedNextBoard =
    Number.isInteger(raw.nextBoardId) && raw.nextBoardId > 0
      ? raw.nextBoardId
      : 1;
  doc.nextBoardId = Math.max(storedNextBoard, maxBoardSeq + 1);
  const storedNextGroup =
    Number.isInteger(raw.nextGroupId) && raw.nextGroupId > 0
      ? raw.nextGroupId
      : 1;
  doc.nextGroupId = Math.max(storedNextGroup, maxGroupSeq + 1);
  const storedNextComp =
    Number.isInteger(raw.nextComponentId) && raw.nextComponentId > 0
      ? raw.nextComponentId
      : 1;
  doc.nextComponentId = Math.max(storedNextComp, maxCompSeq + 1);
  const storedNextPsu =
    Number.isInteger(raw.nextPsuId) && raw.nextPsuId > 0 ? raw.nextPsuId : 1;
  doc.nextPsuId = Math.max(storedNextPsu, maxBrickSeq.psu + 1);
  const storedNextClock =
    Number.isInteger(raw.nextClockId) && raw.nextClockId > 0
      ? raw.nextClockId
      : 1;
  doc.nextClockId = Math.max(storedNextClock, maxBrickSeq.clock + 1);
  const nextLoad = Math.max(
    Number.isInteger(raw.nextLoadId) && raw.nextLoadId > 0 ? raw.nextLoadId : 1,
    maxBrickSeq.load + 1,
  );
  if (nextLoad > 1) doc.nextLoadId = nextLoad;
  // The optional counters, each stored only once its brick has been placed.
  for (const kind of ["ac", "transformer", "adapter", "meter"]) {
    const { counter } = BRICKS[kind];
    const next = Math.max(
      Number.isInteger(raw[counter]) && raw[counter] > 0 ? raw[counter] : 1,
      maxBrickSeq[kind] + 1,
    );
    if (next > 1) doc[counter] = next;
  }
  const storedNextWire =
    Number.isInteger(raw.nextWireId) && raw.nextWireId > 0 ? raw.nextWireId : 1;
  doc.nextWireId = Math.max(storedNextWire, maxWireSeq + 1);
  const storedNextBus =
    Number.isInteger(raw.nextBusId) && raw.nextBusId > 0 ? raw.nextBusId : 1;
  doc.nextBusId = Math.max(storedNextBus, maxBusSeq + 1);
  const storedNextAnnotation =
    Number.isInteger(raw.nextAnnotationId) && raw.nextAnnotationId > 0
      ? raw.nextAnnotationId
      : 1;
  doc.nextAnnotationId = Math.max(storedNextAnnotation, maxAnnSeq + 1);
  const storedNextScope =
    Number.isInteger(raw.nextScopeChannelId) && raw.nextScopeChannelId > 0
      ? raw.nextScopeChannelId
      : 1;
  doc.nextScopeChannelId = Math.max(storedNextScope, maxScopeSeq + 1);
  const storedNextSignal =
    Number.isInteger(raw.nextSignalId) && raw.nextSignalId > 0
      ? raw.nextSignalId
      : 1;
  doc.nextSignalId = Math.max(storedNextSignal, maxSignalSeq + 1);
  const storedNextOutput =
    Number.isInteger(raw.nextOutputId) && raw.nextOutputId > 0
      ? raw.nextOutputId
      : 1;
  doc.nextOutputId = Math.max(storedNextOutput, maxElementSeq.output + 1);
  const storedNextInput =
    Number.isInteger(raw.nextInputId) && raw.nextInputId > 0
      ? raw.nextInputId
      : 1;
  doc.nextInputId = Math.max(storedNextInput, maxElementSeq.input + 1);
  const storedNextData =
    Number.isInteger(raw.nextDataId) && raw.nextDataId > 0 ? raw.nextDataId : 1;
  doc.nextDataId = Math.max(storedNextData, maxElementSeq.data + 1);
  return doc;
}

/** Strict rect overlap — boards may touch edge-to-edge but not intersect. */
/**
 * Do two rects INTERSECT — as opposed to merely touching?
 *
 * The distinction is the whole point: strips are MEANT to sit flush (a kit is
 * `rail · pins · rail`, dovetailed edge to edge), so an overlap test that
 * counted a shared edge would refuse every legal breadboard. A bare `<` gets
 * that right only when the arithmetic is exact — and vertical geometry is
 * MEASURED, not lattice (board-types.js), so it is not. A rail at y -9.03 is
 * 3.50 tall and ends at -5.529999999999999; the pin-board flush under it begins
 * at -5.53. Strictly, those overlap. By 8.9×10^-16.
 *
 * That is not a rounding curiosity — it is silent data loss, because
 * `normalizeDocument` DROPS an overlapping board and cascades away every part
 * seated on it and every wire touching it. It struck about one flush joint in
 * six (only the pairs whose sum happens to round the wrong way), and Fit's
 * whole-desk recentre re-rolls the dice on every load, so a different board
 * vanished each time.
 *
 * So the test carries `mating.js`'s own FLUSH_EPS, which exists for exactly
 * this and is imported rather than restated. An "overlap" thinner than 10^-6 of
 * a pitch (2.5 nanometres) is two strips touching; a real one is at least 0.01,
 * the quantum a board coordinate is held to.
 */
function rectsOverlap(a, b) {
  return (
    a.x < b.x + b.width - FLUSH_EPS &&
    b.x < a.x + a.width - FLUSH_EPS &&
    a.y < b.y + b.height - FLUSH_EPS &&
    b.y < a.y + a.height - FLUSH_EPS
  );
}

/** Is a desk point inside a rect? INCLUSIVE of every edge — unlike
    rectsOverlap, which has to let two strips sit flush without intersecting,
    this asks which strip a free coordinate is drawn over, and a bend right on
    a board's edge is better carried by it than left behind. */
function pointInRect(p, rect) {
  return (
    p.x >= rect.x &&
    p.x <= rect.x + rect.width &&
    p.y >= rect.y &&
    p.y <= rect.y + rect.height
  );
}

/** The edges a directional break travels along. */
const CHAIN_EDGES = Object.freeze({
  forward: Object.freeze(["below", "right"]),
  backward: Object.freeze(["above", "left"]),
});

/**
 * Partition `boards` into the runs that are still mated to one another —
 * used after a break to find which pieces are left holding together. Pure;
 * considers only the boards passed in, so a split can never absorb an
 * outsider that merely happens to sit flush.
 *
 * @returns {Array<Array<object>>} components, each in the given order.
 */
function matedComponents(boards) {
  const pool = [...boards];
  const components = [];
  while (pool.length > 0) {
    const component = [pool.shift()];
    for (let i = 0; i < component.length; i += 1) {
      for (let j = pool.length - 1; j >= 0; j -= 1) {
        if (matingEdge(component[i], pool[j])) {
          component.push(pool.splice(j, 1)[0]);
        }
      }
    }
    components.push(component);
  }
  return components;
}

/**
 * The public methods that only READ the document — the ones that leave
 * `revision` where it was (below). Every other method counts as a change,
 * a new one included, so a method missing here costs the auto-save one
 * signature it did not need, and one listed here wrongly would let a change
 * go unstashed: a method goes on this list only when it writes nothing
 * (tests/desk-doc-revision.test.js calls each and compares the document).
 * Getters are reads by construction (each hands out a copy) and are not
 * counted at all.
 */
export const DESK_DOC_READS = Object.freeze(
  new Set([
    "getBoard",
    "canPlace",
    "canPlaceBrick",
    "canPlaceKit",
    "matingStrips",
    "matedChain",
    "groupMembers",
    "canMoveBoardBy",
    "canMoveBoardsBy",
    "snapBoardsBy",
    "snapKitAt",
    "snapDesignAt",
    "wirePointsOverBoards",
    "getComponent",
    "componentsOnBoard",
    "canPlacePart",
    "occupancyIndex",
    "wiresRidingPart",
    "partsRidingPart",
    "flagsRidingPart",
    "planPartMove",
    "canSetComponentParams",
    "canProbeAt",
    "coveredAddresses",
    "seatKey",
    "getWire",
    "wiresTouching",
    "freeHoles",
    "isHoleFree",
    "canPlaceWire",
    "canReendWire",
    "canMoveWire",
    "canMoveWiresBatch",
    "prepareWireBatchMove",
    "clusterMembers",
    "resolveClusterTargets",
    "wiresRidingCluster",
    "partsRidingCluster",
    "flagsRidingCluster",
    "planClusterRiders",
    "leadShift",
    "getBus",
    "busOfWire",
    "netNameAt",
    "getAnnotation",
    "getSignal",
    "isBoardHole",
    "canPlaceSignalFlag",
    "getIntegration",
    "canPlaceIntegrationTag",
    "getScopeChannel",
    "hasScopeChannel",
    "toJSON",
    "snapshot",
  ]),
);

export class DeskDoc {
  #doc;
  // How many changing calls the document has taken (`revision`).
  #revision = 0;
  // The occupancy index a BATCH of additions shares (pasteDesign): built once
  // and kept in step as each part and wire lands, instead of rebuilt from the
  // whole desk for every one — which made a big paste quadratic (review
  // 2026-10-26: 68.6 s for busy64). Null outside a batch.
  #batchOcc = null;
  // The meter probes resting on a board hole as the last edit left them:
  // `meterId|which|address` → whether a lead was in that hole then
  // (#noteProbes, pressProbesOff).
  #probeHoles = new Map();

  /** @param {object|null} raw - a loaded document (normalized here) or null. */
  constructor(raw = null) {
    this.#doc = normalizeDocument(raw);
    this.#noteProbes();
  }

  /** Copies of the boards on the desk. */
  get boards() {
    return this.#doc.boards.map((b) => ({ ...b }));
  }

  /** A copy of one board, or null. */
  getBoard(id) {
    const b = this.#doc.boards.find((x) => x.id === id);
    return b ? { ...b } : null;
  }

  /**
   * Update a board's Name/Description — the shared Properties dialog's only
   * fields for a board (a strip has no other editable properties; its
   * geometry/rotation is fixed once placed). Present only when non-empty, same
   * omit-when-empty convention as setComponentMeta/schematicPos. Throws
   * NOT_FOUND. Returns a copy.
   */
  setBoardParams(id, patch) {
    const board = this.#doc.boards.find((b) => b.id === id);
    if (!board) throw taggedError(`no board ${id}`, "NOT_FOUND");
    if (typeof patch.name === "string") {
      if (patch.name) board.name = patch.name;
      else delete board.name;
    }
    if (typeof patch.description === "string") {
      if (patch.description) board.description = patch.description;
      else delete board.description;
    }
    return { ...board };
  }

  /**
   * Cut a veroboard's copper at `hole`, or — when it is already cut there —
   * restore it (the Cut Track tool's one click). The list is REPLACED, never
   * edited in place: the node lookup caches on it (breadboard.js). Throws
   * NOT_FOUND for no such board, INVALID_ARG for a hole that cannot be cut
   * (not a veroboard hole). Returns `{ cut }` — true when the click cut.
   */
  toggleCut(id, hole) {
    const board = this.#doc.boards.find((b) => b.id === id);
    if (!board) throw taggedError(`no board ${id}`, "NOT_FOUND");
    if (!isCuttable(board.type, hole)) {
      throw taggedError(`${id}.${hole} is not a cuttable hole`, "INVALID_ARG");
    }
    const had = board.cuts?.includes(hole) ?? false;
    const next = had
      ? board.cuts.filter((h) => h !== hole)
      : normalizeCuts(board.type, [...(board.cuts ?? []), hole]);
    if (next.length > 0) board.cuts = next;
    else delete board.cuts;
    return { cut: !had };
  }

  /** Replace a veroboard's whole cut list (normalized; an empty one drops the
      key). Throws NOT_FOUND. Returns a copy of the board. */
  setCuts(id, cuts) {
    const board = this.#doc.boards.find((b) => b.id === id);
    if (!board) throw taggedError(`no board ${id}`, "NOT_FOUND");
    const next = normalizeCuts(board.type, cuts);
    if (next.length > 0) board.cuts = next;
    else delete board.cuts;
    return { ...board };
  }

  /** The desk rectangles of every brick (PSU, clock) from its def size.
      `ignoreIds` lifts a whole moving SET out at once — a pair of bricks
      dragged together each land over the other's old rect, so ignoring one at
      a time would refuse a move that is perfectly legal. */
  #brickRects({ ignoreId = null, ignoreIds = null } = {}) {
    const skip = ignoreIds ?? (ignoreId == null ? null : new Set([ignoreId]));
    // A multimeter floats over the desk (catalog/meter.js): nothing is ever
    // refused for lying under it.
    return this.#doc.components
      .filter(
        (c) => c.board == null && c.kind !== "meter" && !skip?.has(c.id) && partDef(c.ref)?.size, // prettier-ignore
      )
      .map((c) => {
        const { width, height } = partDef(c.ref).size;
        return { x: c.x, y: c.y, width, height };
      });
  }

  /**
   * Would a `type` board fit at (x, y) (after integer snapping) without
   * overlapping any existing board's outline or PSU brick? `ignoreId`
   * excludes a board from the check (moving over its own footprint is fine).
   */
  canPlace(type, x, y, { ignoreId = null, rot = 0 } = {}) {
    const { width, height } = boardSize(type, normalizeRotation(type, rot));
    // x takes the column lattice (as a placement will); y keeps its two
    // decimals, because a kit's own strips arrive on fractional offsets and
    // rounding them here would check a rect the board will never occupy.
    const rect = { x: placeX(x, type), y: boardCoord(y), width, height };
    return (
      this.#doc.boards.every(
        (b) => b.id === ignoreId || !rectsOverlap(rect, outlineRect(b)),
      ) && this.#brickRects().every((r) => !rectsOverlap(rect, r))
    );
  }

  /**
   * Would a brick (`ref` sizing) fit at (x, y) — x snapped to whole pitches,
   * y to the 0.01 grid, exactly as `moveBrick` stores it — without covering a
   * board or another brick? Testing a rounded y let a brick recentred onto a
   * fractional row slide half a pitch into a board.
   */
  canPlaceBrick(
    ref,
    x,
    y,
    { ignoreId = null, ignoreIds = null, boardShift = null } = {},
  ) {
    // The multimeter floats over boards and bricks alike.
    if (partDef(ref)?.kind === "meter") return true;
    const { width, height } = partDef(ref).size;
    const rect = { x: Math.round(x), y: boardCoord(y), width, height };
    // `boardShift` ({ids, dx, dy}): boards moving in the SAME gesture, judged
    // where they will be — a brick carried beside its circuit must not be
    // refused for landing where a board it travels with used to sit.
    const at = (b) =>
      boardShift?.ids.has(b.id)
        ? { ...b, x: boardCoord(b.x + boardShift.dx), y: boardCoord(b.y + boardShift.dy) } // prettier-ignore
        : b;
    return (
      this.#doc.boards.every((b) => !rectsOverlap(rect, outlineRect(at(b)))) &&
      this.#brickRects({ ignoreId, ignoreIds }).every(
        (r) => !rectsOverlap(rect, r),
      )
    );
  }

  /**
   * Add a board (coordinates snapped to integers). Throws INVALID_TYPE /
   * INVALID_ARG / OVERLAP. Returns a copy of the new board.
   *
   * BOARD NAMES: a strip is born NAMED after its id (`bb2`), here and in
   * addKit. The id is what every message and hole address quotes (`bb2.a12`,
   * a short on `bb2.+`), and nothing drawn on the desk says which strip that
   * is — so the Properties dialog's Name is where the reader finds it, and a
   * rename is still theirs to make. An ordinary name, not a derived one:
   * boards loaded without one stay without one, and pasteDesign re-names a
   * copy whose name was still its source's id (see there).
   */
  addBoard(type, x, y, rot = 0) {
    spec(type); // validates — throws INVALID_TYPE
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw taggedError("board position must be finite", "INVALID_ARG");
    }
    const turn = normalizeRotation(type, rot);
    if (!this.canPlace(type, x, y, { rot: turn })) {
      throw taggedError(
        `a ${type} board at ${placeX(x, type)},${boardCoord(y)} overlaps an existing board`,
        "OVERLAP",
      );
    }
    const id = `bb${this.#doc.nextBoardId++}`;
    const board = {
      id,
      type,
      x: placeX(x, type),
      y: boardCoord(y),
      rot: turn,
      group: null,
      name: id, // BOARD NAMES, above
    };
    this.#doc.boards.push(board);
    return { ...board };
  }

  // ── Kits & groups ───────────────────────────────────────────────────────

  /**
   * The strips a kit places, resolved to absolute integer positions. Pure —
   * used for the placement ghost as well as for the real add.
   *
   * @returns {Array<{type:string,x:number,y:number}>}
   */
  static kitPlacements(kitKey, x, y, rot = 0, flipRails = false) {
    const kit = kitFor(kitKey);
    if (!kit) throw taggedError(`unknown kit: ${kitKey}`, "INVALID_TYPE");
    // A one-board kit lands where that board would (a veroboard's holes on
    // the lattice); an assembled kit's strips all share the whole-pitch x.
    const ox = placeX(x, kit.strips.length === 1 ? kit.strips[0].type : null);
    const oy = boardCoord(y);
    // A kit turns only if EVERY strip in it can: in practice the lone-rail
    // kits. An assembled board holds a pin-board, so it stays flat, and the
    // preset offsets are only ever meaningful at 0. An assembled kit's own
    // rail strips may instead be FLIPPED 180° independently of the pin-board
    // — same footprint (boardSize is identical at 0/180), reversed polarity
    // order — which is what `flipRails` asks for. `turn` wins over a flip
    // when both are somehow asked for; in practice a loose-rail "kit" (the
    // only one `turn` ever applies to) never sets `flipRails`.
    const turn = DeskDoc.canRotateKit(kitKey) ? normalizeRotation(kit.strips[0].type, rot) : 0; // prettier-ignore
    return kit.strips.map((s) => ({
      type: s.type,
      x: ox + s.dx,
      y: boardCoord(oy + s.dy),
      rot: turn !== 0 ? turn : flipRails && canRotate(s.type) ? 180 : 0,
    }));
  }

  /** Can this kit be placed on its side? Only one made purely of rails. */
  static canRotateKit(kitKey) {
    const kit = kitFor(kitKey);
    if (!kit) throw taggedError(`unknown kit: ${kitKey}`, "INVALID_TYPE");
    return kit.strips.every((s) => canRotate(s.type));
  }

  /**
   * Can this kit's own rail strips be flipped 180° independently, leaving
   * the pin-board fixed? True for an assembled kit with at least one rail
   * (Full 830, Half 400) — false for a bare pin-board (Tiny 170, no rails to
   * flip) and false for a loose single-strip kit (rail-full / rail-half),
   * which already owns R for its own whole-kit rotation via `canRotateKit`.
   */
  static canFlipKitRails(kitKey) {
    const kit = kitFor(kitKey);
    if (!kit) throw taggedError(`unknown kit: ${kitKey}`, "INVALID_TYPE");
    return (
      !DeskDoc.canRotateKit(kitKey) && kit.strips.some((s) => canRotate(s.type))
    );
  }

  /** The bounding box of a kit, for centring the placement ghost. */
  static kitOutline(kitKey, rot = 0) {
    const strips = DeskDoc.kitPlacements(kitKey, 0, 0, rot);
    const sized = strips.map((s) => ({ s, size: boardSize(s.type, s.rot) }));
    // Quantized like a board origin: the height is a sum of measured strips
    // (3.50 + 14.02 + 3.50), and a sum of 0.01-grid values need not land on
    // the grid in binary — this outline is compared against, and drawn, as a
    // stated size.
    // Measured from the kit's own left edge: a veroboard is placed at the
    // fraction that lands its holes on the lattice, not at 0.
    const left = Math.min(...sized.map(({ s }) => s.x));
    return {
      width: boardCoord(
        Math.max(...sized.map(({ s, size }) => s.x + size.width)) - left,
      ),
      height: boardCoord(
        Math.max(...sized.map(({ s, size }) => s.y + size.height)),
      ),
    };
  }

  /** Would every strip of a kit fit at (x, y)? All-or-nothing. */
  canPlaceKit(kitKey, x, y, rot = 0, flipRails = false) {
    return DeskDoc.kitPlacements(kitKey, x, y, rot, flipRails).every((s) =>
      this.canPlace(s.type, s.x, s.y, { rot: s.rot }),
    );
  }

  /**
   * Place a whole breadboard: every strip of the kit, seated at its preset
   * offset and joined into one group so they drag as a unit. `flipRails`
   * mirrors the kit's own rail strips 180° in place (positive/negative row
   * order reversed) while the pin-board stays put — a ghost-only choice
   * baked in at drop time, never editable on a placed board. Throws
   * INVALID_TYPE / INVALID_ARG / OVERLAP — nothing is added on failure.
   *
   * @returns {Array<object>} copies of the new strips, in kit order.
   */
  addKit(kitKey, x, y, rot = 0, flipRails = false) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw taggedError("kit position must be finite", "INVALID_ARG");
    }
    const placements = DeskDoc.kitPlacements(kitKey, x, y, rot, flipRails);
    if (!this.canPlaceKit(kitKey, x, y, rot, flipRails)) {
      throw taggedError(
        `a ${kitKey} breadboard at ${placeX(x)},${boardCoord(y)} overlaps an existing board`,
        "OVERLAP",
      );
    }
    // A lone strip needs no group — grouping starts at two.
    const group = placements.length > 1 ? `g${this.#doc.nextGroupId++}` : null;
    const added = placements.map((p) => {
      const id = `bb${this.#doc.nextBoardId++}`;
      return { id, type: p.type, x: p.x, y: p.y, rot: p.rot, group, name: id };
    });
    this.#doc.boards.push(...added);
    return added.map((b) => ({ ...b }));
  }

  /**
   * The strips `id` dovetails with: same width and same left edge (the real
   * part only mates with its own size), and edge-to-edge in y with no gap.
   * Pure — the geometric half of the mating rule, with no group side effects.
   *
   * @returns {Array<object>} copies of the mating strips, document order.
   */
  matingStrips(id) {
    const board = this.#doc.boards.find((b) => b.id === id);
    if (!board) return [];
    return this.#doc.boards
      .filter((b) => b.id !== id && matingEdge(board, b) !== null)
      .map((b) => ({ ...b }));
  }

  /**
   * The strips that travel with `id` when a snap is broken directionally:
   * `id` itself plus everything reachable from it through mating edges that
   * point only one way — `forward` (below / right) or `backward` (above /
   * left). Whatever lies the other way is left behind.
   *
   * The walk stays INSIDE `id`'s group, so a strip merely resting flush
   * against the stack — placed there, never snapped — is never dragged along.
   * Throws INVALID_ARG on an unknown direction.
   *
   * @returns {Array<object>} copies of the chain, in document order.
   */
  matedChain(id, direction = "forward") {
    const edges = CHAIN_EDGES[direction];
    if (!edges) {
      throw taggedError(`unknown chain direction: ${direction}`, "INVALID_ARG");
    }
    const pool = this.groupMembers(id);
    if (pool.length === 0) return [];
    const chained = new Set([id]);
    // Breadth-first along one-way edges: reaching a strip does not license
    // travelling back up from it, so a break only ever runs one way.
    const queue = [pool.find((b) => b.id === id)];
    while (queue.length > 0) {
      const current = queue.shift();
      for (const b of pool) {
        if (chained.has(b.id)) continue;
        if (!edges.includes(matingEdge(current, b))) continue;
        chained.add(b.id);
        queue.push(b);
      }
    }
    return pool.filter((b) => chained.has(b.id));
  }

  /**
   * Mate `id` with every strip it dovetails against: the strips — and the
   * whole group each already belongs to — are united under one group id, so
   * the stack drags as a unit. Reuses an existing group when there is one
   * (the oldest, in document order) and mints `g<n>` only for a stack of
   * loose strips. A no-op returning null when `id` touches nothing.
   *
   * @returns {string|null} the resulting group id.
   */
  joinMatedGroup(id) {
    const mates = this.matingStrips(id);
    if (mates.length === 0) return null;
    // The union spans each mate's whole group, so mating with one strip of an
    // assembled board joins the entire board, not just the strip touched.
    const groups = new Set(mates.map((b) => b.group).filter((g) => g != null));
    const ids = new Set([id, ...mates.map((b) => b.id)]);
    const members = this.#doc.boards.filter(
      (b) => ids.has(b.id) || (b.group != null && groups.has(b.group)),
    );
    const group =
      members.find((b) => b.group != null)?.group ??
      `g${this.#doc.nextGroupId++}`;
    for (const b of members) b.group = group;
    return group;
  }

  /**
   * The strips that move with `id`: its whole group, or just itself when it
   * is loose. Always includes `id`; empty when there is no such board.
   */
  groupMembers(id) {
    const board = this.#doc.boards.find((b) => b.id === id);
    if (!board) return [];
    if (board.group == null) return [{ ...board }];
    return this.#doc.boards
      .filter((b) => b.group === board.group)
      .map((b) => ({ ...b }));
  }

  /**
   * Would translating `id`'s group by (dx, dy) — integers — clear every board
   * and brick outside the group?
   */
  canMoveBoardBy(id, dx, dy) {
    return this.canMoveBoardsBy(
      this.groupMembers(id).map((b) => b.id),
      dx,
      dy,
    );
  }

  /**
   * Would translating exactly `ids` by (dx, dy) clear every board and brick
   * that is NOT moving? False when any id is unknown.
   *
   * The delta is NOT rounded to whole pitches here (nor in `moveBoardsBy`,
   * which must land on exactly the position this cleared). A gesture supplies
   * a whole-pitch delta of its own accord — that is what makes a board drag
   * step pitch by pitch — but the magnetic pull is then ADDED to it, and a
   * dovetail is only ever flush at an exact value: against a 3.50-tall rail
   * or a 14.02-tall pin-board that value is fractional, and rounding it away
   * here is a snap that lands short and silently fails to mate.
   */
  canMoveBoardsBy(ids, dx, dy, { brickTargets = null, movers = null } = {}) {
    const moving = new Set(ids);
    const members = this.#doc.boards.filter((b) => moving.has(b.id));
    if (members.length === 0 || members.length !== moving.size) return false;
    const rects = members.map((b) =>
      outlineRect({
        ...b,
        x: boardCoord(b.x + dx),
        y: boardCoord(b.y + dy),
      }),
    );
    const others = this.#doc.boards.filter((b) => !moving.has(b.id));
    // `brickTargets` (id → {x, y}): bricks moving in the SAME gesture, judged
    // where they will land rather than where they stand.
    const bricks = [
      ...this.#brickRects({
        ignoreIds: brickTargets ? new Set(brickTargets.keys()) : null,
      }),
      ...[...(brickTargets ?? new Map())].map(([id, pos]) => {
        const { width, height } = partDef(this.getComponent(id).ref).size;
        return { x: Math.round(pos.x), y: boardCoord(pos.y), width, height };
      }),
    ];
    return (
      rects.every(
        (rect) =>
          others.every((b) => !rectsOverlap(rect, outlineRect(b))) &&
          bricks.every((r) => !rectsOverlap(rect, r)),
      ) && !this.#bentLeadsCollide(moving, dx, dy, movers)
    );
  }

  /**
   * Would translating the strips in `moving` by (dx, dy) put a BENT lead on a
   * hole that is already spoken for?
   *
   * Everything else on a desk is an address, so it rides its strip and keeps
   * its place relative to everything beside it. A rotated part's free lead
   * (and an oscillator can's corners) is the exception: it is resolved
   * GEOMETRICALLY against whatever lies under its bend, so moving a strip
   * under it — or moving the part's own strip out from under the lead — can
   * land it on a hole holding a wire end, another part's pin, or under a wide
   * chip's body. The rectangle check could not see that, so the drop went
   * through and `normalizeDocument` silently dropped a part on the next load.
   * A lead that lands where it already was (or on nothing — floating is
   * legal) is never the move's doing.
   */
  #bentLeadsCollide(moving, dx, dy, movers = null) {
    const doc = this.#doc;
    // EVERY strip moving is the whole desk translated: a lead resolves
    // against strips alone, and it and every strip it could reach travel
    // together, so each keeps the hole it had. (A Select-All drag, or a desk
    // that is one snapped group, asked every bent lead twice a sample.)
    if (doc.boards.every((b) => moving.has(b.id))) return false;
    const seated = doc.components.filter(
      (c) => c?.kind === "chip" || c?.kind === "discrete",
    );
    // A part moving in the same gesture (`movers`) re-lands its own leads —
    // the gesture's batch check judges those, among the moved strips.
    const bent = seated.filter(
      (c) =>
        !movers?.has(c.id) &&
        partPinHoles(c.ref, c.anchor, c.params)?.some((p) => p.offset),
    );
    if (!bent.length) return false;
    const after = {
      ...doc,
      boards: doc.boards.map((b) =>
        moving.has(b.id)
          ? { ...b, x: boardCoord(b.x + dx), y: boardCoord(b.y + dy) }
          : b,
      ),
    };
    // The leads the move actually re-lands: a lead that resolves where it
    // already was (or onto nothing — floating is legal) is not the move's
    // doing. Most drags have none, and then nothing else is computed.
    const moved = [];
    for (const c of bent) {
      const before = partPinAddresses(doc, c) ?? [];
      for (const [i, p] of (partPinAddresses(after, c) ?? []).entries()) {
        if (p.address != null && p.address !== before[i]?.address) {
          moved.push(p.address);
        }
      }
    }
    if (!moved.length) return false;
    // Every claim on every point once the strips have moved: bodies, pins,
    // wire ends, flags and tags — one hole, one lead.
    const claims = new Map();
    const claim = (a) => {
      if (typeof a === "string") claims.set(a, (claims.get(a) ?? 0) + 1);
    };
    // A mover's claims are left to the batch check too: it is LEAVING the
    // holes it holds now, and that check judges where it lands — against the
    // leads re-landed here (`prepareClusterMove`'s `boardShift`).
    for (const c of seated) {
      if (movers?.has(c.id)) continue;
      for (const a of partCoverAddresses(after, c)) claim(a);
      for (const p of partPinAddresses(after, c) ?? []) claim(p.address);
    }
    for (const w of doc.wires) {
      claim(w?.from);
      claim(w?.to);
    }
    for (const sig of doc.signals ?? []) claim(sig?.flag?.anchor);
    for (const el of doc.integrations ?? []) {
      for (const tag of Object.values(el?.tags ?? {})) claim(tag?.anchor);
    }
    return moved.some((a) => claims.get(a) > 1);
  }

  /**
   * The magnetic pull on a drag: the EXTRA (dx, dy) — at most SNAP_RANGE on
   * either axis — that would land `ids`, already translated by (dx, dy),
   * flush against a strip outside the set that it can dovetail with.
   * `{dx: 0, dy: 0}` when nothing is in range or a pair is already flush.
   *
   * Pure geometry: it neither moves nor groups anything, and says nothing
   * about legality — the caller applies the pull only if it likes the result.
   */
  snapBoardsBy(ids, dx, dy) {
    const moving = new Set(ids);
    const [members, others] = [
      this.#doc.boards.filter((b) => moving.has(b.id)),
      this.#doc.boards.filter((b) => !moving.has(b.id)),
    ];
    if (members.length === 0) return { dx: 0, dy: 0 };
    return quantizePull(
      snapCorrection(
        members.map(
          (b) =>
          outlineRect({ ...b, x: boardCoord(b.x + dx), y: boardCoord(b.y + dy) }), // prettier-ignore
        ),
        others.map(outlineRect),
      ),
    );
  }

  /** The same magnetic pull, for a kit not yet placed (the ghost). */
  snapKitAt(kitKey, x, y, rot = 0) {
    return quantizePull(
      snapCorrection(
        DeskDoc.kitPlacements(kitKey, x, y, rot).map(outlineRect),
        this.#doc.boards.map(outlineRect),
      ),
    );
  }

  /** The same magnetic pull, for a design clip about to be pasted at `shift`
      (Feature 240) — so a pasted design mates exactly like a placed kit. */
  snapDesignAt(clip, shift) {
    return snapDesign(clip, shift, this.#doc.boards.map(outlineRect));
  }

  /**
   * Translate `id`'s whole group by (dx, dy). Throws NOT_FOUND /
   * INVALID_ARG / OVERLAP. Returns copies of every moved strip.
   */
  moveBoardBy(id, dx, dy) {
    if (!this.#doc.boards.some((b) => b.id === id)) {
      throw taggedError(`no board ${id}`, "NOT_FOUND");
    }
    return this.moveBoardsBy(
      this.groupMembers(id).map((b) => b.id),
      dx,
      dy,
    );
  }

  /**
   * Which routed waypoints a move of `ids` CARRIES, as `Map<wireId, number[]>`
   * — indices into that wire's own `points`, ascending, wires with none
   * omitted.
   *
   * A wire's ENDS are addresses, so they ride their board for free; its
   * waypoints are free desk coordinates and are therefore the one part of a
   * wire a board move has to carry BY HAND (the same reason `translateAll`
   * and `pasteDesign` shift them explicitly). Leave them and dragging a board
   * drags its wiring out of the routing the user drew.
   *
   * A wire whose two ends BOTH ride the move — each plugged into one of the
   * moving strips, or into anything else in `owners` (a gesture adds its
   * moving bricks) — travels whole: nothing about it changes relative to
   * anything, so every bend comes along, the ones out in the gaps included.
   * (Carrying only the bends over a strip tore a Select-All drag's auto-routed
   * wiring into zig-zags.)
   *
   * Every OTHER wire — stretched between the move and something staying put —
   * is judged PER POINT, because position is the only thing a waypoint has to
   * say where it belongs: a bend drawn over a moving board was drawn around
   * what is ON that board and travels with it, while one out in the free space
   * belongs to the gap that just changed size and stays where it was put. So
   * such a wire may well carry some of its bends and not others. Containment is
   * inclusive — a point exactly on the moving set's edge rides, since carrying
   * it is the lesser surprise.
   *
   * `exclude` names wires the caller moves some other way (a gesture's
   * selected wires and riders), so no bend is ever shifted twice.
   *
   * Pure: nothing is mutated, and the answer is about where the points sit
   * NOW, so a caller previewing a drag must read it BEFORE anything moves.
   *
   * @param {Iterable<string>} ids - the moving strips
   * @param {{owners?: Iterable<string>, exclude?: Set<string>}} [opts]
   */
  wirePointsOverBoards(ids, { owners = null, exclude = null } = {}) {
    const moving = new Set(ids);
    const riders = new Set([...moving, ...(owners ?? [])]);
    const rects = this.#doc.boards
      .filter((b) => moving.has(b.id))
      .map(outlineRect);
    const carried = new Map();
    if (riders.size === 0) return carried;
    const rides = (address) => riders.has(parseAddress(address)?.boardId);
    for (const wire of this.#doc.wires) {
      const points = wire.points ?? [];
      if (points.length === 0 || exclude?.has(wire.id)) continue;
      if (rides(wire.from) && rides(wire.to)) {
        carried.set(
          wire.id,
          points.map((_, i) => i),
        );
        continue;
      }
      const indices = [];
      points.forEach((p, i) => {
        if (rects.some((r) => pointInRect(p, r))) indices.push(i);
      });
      if (indices.length > 0) carried.set(wire.id, indices);
    }
    return carried;
  }

  /** Shift the waypoints `carried` names (`wirePointsOverBoards`'s map) by
      (dx, dy), in place. */
  #shiftCarriedPoints(carried, dx, dy) {
    if (!carried?.size) return;
    const byId = new Map(this.#doc.wires.map((w) => [w.id, w]));
    for (const [wireId, indices] of carried) {
      const points = byId.get(wireId)?.points ?? [];
      for (const i of indices) {
        if (!points[i]) continue;
        points[i].x = wireCoord(points[i].x + dx);
        points[i].y = wireCoord(points[i].y + dy);
      }
    }
  }

  /**
   * Translate exactly `ids` by (dx, dy) — the rigid move behind a directional
   * break. When the set is only PART of a group, the snap breaks: both halves
   * are re-grouped from what is still mated within each, so a run that stays
   * whole keeps travelling as a unit and a strip left on its own goes loose.
   * A group id is never reused across a break. Every routed waypoint over the
   * moving strips travels with them (`wirePointsOverBoards`), in the SAME
   * mutation, so a board and the routing drawn over it are one undo step.
   * A gesture that froze that set at its press (and previewed it) hands it
   * over as `carried`, so the drop moves exactly the bends the preview did —
   * re-reading it here, after the gesture's other writes, could catch a bend
   * a selected wire had just carried over a strip and shift it twice.
   * Throws NOT_FOUND / INVALID_ARG / OVERLAP — nothing moves on failure.
   *
   * @param {{carried?: Map<string, number[]>}} [opts]
   * @returns {Array<object>} copies of every moved strip, document order.
   */
  moveBoardsBy(ids, dx, dy, { carried = null } = {}) {
    const moving = new Set(ids);
    const members = this.#doc.boards.filter((b) => moving.has(b.id));
    if (members.length === 0 || members.length !== moving.size) {
      throw taggedError(`no such board in [${[...moving]}]`, "NOT_FOUND");
    }
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
      throw taggedError("board delta must be finite", "INVALID_ARG");
    }
    if (!this.canMoveBoardsBy(ids, dx, dy)) {
      throw taggedError(`moving [${[...moving]}] overlaps a board`, "OVERLAP");
    }
    // A group that is only partly moving is being torn apart — note it before
    // the translation, and re-derive both halves from geometry afterwards.
    const torn = [
      ...new Set(members.map((b) => b.group).filter((g) => g != null)),
    ].filter((g) =>
      this.#doc.boards.some((b) => b.group === g && !moving.has(b.id)),
    );
    // Which bends ride, read BEFORE the translation — the rule is where each
    // point sits now, not where the strips are about to be.
    const riding = carried ?? this.wirePointsOverBoards(ids);
    const moved = [];
    for (const b of this.#doc.boards) {
      if (!moving.has(b.id)) continue;
      b.x = boardCoord(b.x + dx);
      b.y = boardCoord(b.y + dy);
      moved.push(b);
    }
    this.#shiftCarriedPoints(riding, dx, dy);
    for (const group of torn) this.#regroupAfterBreak(group, moving);
    return moved.map((b) => ({ ...b }));
  }

  /**
   * Re-derive `group` after a break: each half is split into the runs still
   * mated within it, and every run of two or more gets a FRESH id (a lone
   * strip goes loose). Fresh on both sides, so the two halves can never come
   * out sharing an id and silently stay one unit.
   */
  #regroupAfterBreak(group, movedIds) {
    const members = this.#doc.boards.filter((b) => b.group === group);
    this.#regroupRuns(members.filter((b) => movedIds.has(b.id)));
    this.#regroupRuns(members.filter((b) => !movedIds.has(b.id)));
  }

  /**
   * Split a set of strips into the runs still mated within it, minting a fresh
   * group id per run of two or more and going loose for a lone strip. The one
   * place a group id is ever (re)assigned after a break.
   */
  #regroupRuns(members) {
    for (const run of matedComponents(members)) {
      const id = run.length > 1 ? `g${this.#doc.nextGroupId++}` : null;
      for (const b of run) b.group = id;
    }
  }

  /**
   * Move a board to an absolute position (coordinates snapped to integers).
   * Throws NOT_FOUND / INVALID_ARG / OVERLAP. Returns a copy of the updated
   * board. Unlike `moveBoardsBy` — which is what the drag gesture commits
   * through — this carries no routed waypoints with it; anything that moves a
   * strip a USER is looking at wants the relative form.
   */
  moveBoard(id, x, y) {
    const board = this.#doc.boards.find((b) => b.id === id);
    if (!board) throw taggedError(`no board ${id}`, "NOT_FOUND");
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw taggedError("board position must be finite", "INVALID_ARG");
    }
    // The strip keeps its angle, so the overlap check has to use it — an
    // upright rail sweeps a 3×64 box, not a 64×3 one.
    if (!this.canPlace(board.type, x, y, { ignoreId: id, rot: board.rot })) {
      throw taggedError(
        `moving ${id} to ${placeX(x, board.type)},${boardCoord(y)} overlaps another board`,
        "OVERLAP",
      );
    }
    const group = board.group;
    board.x = placeX(x, board.type);
    board.y = boardCoord(y);
    // Moving one strip of a group can open a gap the group id would still span,
    // dragging the now-disconnected strips as one unit. Tear it out and
    // re-derive both halves from what is still mated (as moveBoardsBy does).
    if (
      group != null &&
      this.#doc.boards.some((b) => b.group === group && b.id !== id)
    ) {
      this.#regroupAfterBreak(group, new Set([id]));
    }
    return { ...board };
  }

  /**
   * Remove a strip AND everything SEATED on it — components anchored to it
   * and every wire with an endpoint on it (the UI confirms first when
   * anything would go with it). Throws NOT_FOUND.
   *
   * A part anchored on a NEIGHBOURING strip whose free lead happens to reach
   * into this one survives untouched, keeping its position: the lead simply
   * stops resolving to a hole and floats. That is why removal keys on
   * `c.board` (where the part is seated) and never on where a lead lands.
   *
   * Pulling a strip out of the middle of a group BREAKS it exactly as tearing
   * one off does — what is left may no longer touch. So the survivors are
   * re-derived from what is still mated among them, or the two halves would go
   * on dragging as one unit across the gap the removal just opened.
   */
  removeBoard(id) {
    const i = this.#doc.boards.findIndex((b) => b.id === id);
    if (i === -1) throw taggedError(`no board ${id}`, "NOT_FOUND");
    const [removed] = this.#doc.boards.splice(i, 1);
    for (const c of this.#doc.components) {
      if (c.board === id) this.#detachAnnotations(c.id);
    }
    this.#doc.components = this.#doc.components.filter((c) => c.board !== id);
    this.#doc.wires = this.#doc.wires.filter((w) => !this.#wireTouches(w, id));
    this.#detachSignalFlags(id);
    this.#detachIntegrationTags(id);
    this.#detachProbes(id, { boards: [removed], components: [] });
    this.#pruneBusesToWires();
    if (removed.group != null) {
      this.#regroupRuns(
        this.#doc.boards.filter((b) => b.group === removed.group),
      );
    }
  }

  /** Does a wire endpoint belong to this owner (board or PSU)? */
  #wireTouches(wire, ownerId) {
    return (
      parseAddress(wire.from)?.boardId === ownerId ||
      parseAddress(wire.to)?.boardId === ownerId
    );
  }

  // ── Components (chips now; discrete parts in Feature 60) ────────────────

  /** Copies of the components on the desk. */
  get components() {
    return this.#doc.components.map((c) => ({ ...c }));
  }

  /** A copy of one component, or null. */
  getComponent(id) {
    const c = this.#doc.components.find((x) => x.id === id);
    return c ? { ...c } : null;
  }

  /** Copies of the components seated on one board. */
  componentsOnBoard(boardId) {
    return this.#doc.components
      .filter((c) => c.board === boardId)
      .map((c) => ({ ...c }));
  }

  /**
   * May a board part (chip or discrete) seat here? Delegates to occupancy —
   * the single collision authority. `ignoreId` excludes one component's own
   * pins (moves).
   */
  canPlacePart(
    ref,
    boardId,
    anchor,
    { ignoreId = null, params = null, occupancy = null } = {},
  ) {
    return canPlacePart(this.#doc, {
      ref,
      board: boardId,
      anchor,
      params,
      ignoreId,
      occupancy,
    });
  }

  /**
   * The desk's occupancy index as it stands — for a GESTURE to build once at
   * its press and hand to the checks it repeats every move (`canPlacePart`,
   * `canReendWire`, `canMoveWire` take it as `occupancy`), instead of each
   * check rebuilding it from the whole desk (review 2026-10-26: a solo drag
   * on a big desk spent most of its time there). Valid until the document
   * changes — a gesture's own drop is what changes it.
   */
  occupancyIndex() {
    return buildOccupancy(this.#doc);
  }

  /**
   * Seat a board part: pin 1 at `anchor` on `boardId` (chips row e;
   * discretes any grid row). Params are coerced through the def's contract.
   * Throws INVALID_KIND / INVALID_REF / NOT_FOUND / ILLEGAL_PLACEMENT.
   * Returns a copy.
   */
  addComponent({ kind, ref, board: boardId, anchor, params = {} }) {
    if (kind !== "chip" && kind !== "discrete") {
      throw taggedError(`unsupported component kind: ${kind}`, "INVALID_KIND");
    }
    const def = partDef(ref);
    if (!def || def.kind !== kind) {
      throw taggedError(`unknown ${kind} ref: ${ref}`, "INVALID_REF");
    }
    if (!this.#doc.boards.some((b) => b.id === boardId)) {
      throw taggedError(`no board ${boardId}`, "NOT_FOUND");
    }
    // Normalize FIRST so a rotated resistor's pins (which depend on rot/end)
    // are validated against the params that will actually be stored.
    const normalized = normalizeParams(def, params);
    const occ = this.#batchOcc;
    if (
      !canPlacePart(this.#doc, {
        ref,
        board: boardId,
        anchor,
        params: normalized,
        occupancy: occ,
      })
    ) {
      throw taggedError(
        `a ${ref} cannot seat at ${boardId}.${anchor}`,
        "ILLEGAL_PLACEMENT",
      );
    }
    const component = {
      id: `c${this.#doc.nextComponentId++}`,
      kind,
      ref,
      board: boardId,
      anchor,
      params: normalized,
    };
    this.#doc.components.push(component);
    if (occ) {
      // Claimed as buildOccupancy would: the body's cover, then the pins.
      for (const address of partCoverAddresses(this.#doc, component)) {
        occ.set(address, { kind: "body", componentId: component.id });
      }
      for (const { pin, address } of partPinAddresses(this.#doc, component) ??
        []) {
        if (address != null) occ.set(address, { kind: "pin", componentId: component.id, pin }); // prettier-ignore
      }
    }
    return { ...component };
  }

  /**
   * Re-seat a board part (same or another board). `wide` — a DIP's only — says
   * whether a seat with pin 1 in row e is the 600-mil e/i seat (seating.js
   * `partSeatAt` answers it; footprints.js `withSeat` keeps it in the chip's
   * params); left out, the part keeps the params it has. Throws NOT_FOUND /
   * INVALID_KIND (PSUs move with movePsu) / ILLEGAL_PLACEMENT. Returns a
   * copy of the updated component.
   */
  moveComponent(id, boardId, anchor, { wide } = {}) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    if (comp.board == null) {
      // A desk-level brick (PSU / clock) is repositioned with moveBrick.
      throw taggedError(`use moveBrick for ${id}`, "INVALID_KIND");
    }
    if (!this.#doc.boards.some((b) => b.id === boardId)) {
      throw taggedError(`no board ${boardId}`, "NOT_FOUND");
    }
    const params = this.#seatParams(comp, anchor, wide);
    if (
      !this.canPlacePart(comp.ref, boardId, anchor, { ignoreId: id, params })
    ) {
      throw taggedError(
        `${id} cannot seat at ${boardId}.${anchor}`,
        "ILLEGAL_PLACEMENT",
      );
    }
    comp.board = boardId;
    comp.anchor = anchor;
    comp.params = params;
    return { ...comp };
  }

  /** A component's params re-seated with pin 1 at `anchor`: a DIP's e/i
      marker as `wide` says (footprints.js `withSeat`), when it says; every
      other part's, and a DIP's without `wide`, as they are. Back through the
      def's own contract, which has the last word: the 7-segment digit is
      wide in every seat (`alwaysWide`), and a seat in row d is no reason to
      strip that from it — stripped, its next move into row e seated it
      narrow, its legs 0.3 in apart. */
  #seatParams(comp, anchor, wide) {
    const def = partDef(comp.ref);
    if (!def?.package || wide === undefined) return comp.params;
    return normalizeParams(
      def,
      withSeat(def.package, comp.params, String(anchor).charAt(0), wide),
    );
  }

  // ── Re-seating a part WITH its wiring (Feature 290 — every part drag) ──────

  /**
   * The wires riding part `id` — every wire end sitting in a node one of its
   * pins occupies, as `[{ wireId, ends }]`. Read once at pointerdown and frozen
   * for the gesture; see model/part-move.js for why the set must not be
   * recomputed per pointer sample.
   */
  wiresRidingPart(id) {
    return wiresRidingPart(this.#doc, id);
  }

  /** The two-terminal PARTS riding part `id` — a rotatable part with a LEAD in
      one of its nodes, as `[{ id, pins }]`. Frozen with the wires. */
  partsRidingPart(id) {
    return partsRidingPart(this.#doc, id);
  }

  /** The planted signal flags and Output/Input tags riding part `id` — each in
      a node one of its pins occupies, as `[{ kind, id, key?, address,
      memberId }]`. Frozen with the wires. */
  flagsRidingPart(id) {
    return flagsRidingPart(this.#doc, id);
  }

  /**
   * Where `target.riding`/`target.ridingParts` land when part `id` re-seats at
   * `target.board`.`target.anchor`: `{ moves, points, parts, resolved }`. Pure
   * planning — nothing is mutated, and a plan is not a legality verdict: the
   * caller still runs it past a prepared batch check (occupancy) before
   * committing. `target.params` is for the one mover whose FORM changes as it
   * moves (a rotatable part's body drag); see model/part-move.js.
   */
  planPartMove(id, target) {
    return planPartMove(this.#doc, { id, ...target });
  }

  /**
   * Re-seat a part AND carry everything riding it — wires, and the leads of the
   * two-terminal parts attached to it — as ONE mutation, so ⌘Z restores them
   * together, because they were never two edits. Rolls back wholly on any
   * refusal: half a move would cut the connections it left behind.
   *
   * A solo wired part drag IS a one-member cluster, so this is the cluster commit
   * with the dragged part put at the head of the placements — one transaction
   * rule and one legality rule for both gestures rather than two that could
   * drift apart. `plan` is a `planPartMove` result; `wide` is moveComponent's.
   * Throws ILLEGAL_PLACEMENT.
   *
   * @returns {{component:object, wires:Array}} copies of what moved.
   */
  moveComponentWithWires(id, boardId, anchor, plan, { wide } = {}) {
    const comp = this.#doc.components.find((c) => c.id === id);
    const params = comp ? this.#seatParams(comp, anchor, wide) : undefined;
    const head = { id, board: boardId, anchor };
    if (comp && params !== comp.params) head.params = params;
    const { components, wires } = this.moveClusterWithWires(
      [head, ...(plan?.parts ?? [])],
      plan,
    );
    return { component: components[0], wires };
  }

  /**
   * Reposition a rotatable two-terminal part (resistor) by BOTH ends at once —
   * the rigid drag/rotate commit. Pin 1 seats in `anchor` on `boardId`; pin 2
   * is `end`, a `{dx, dy}` lead bend measured in pitch units from that hole,
   * so it may reach onto a NEIGHBOURING strip (typically a power rail). Both
   * ends must land in free, real, distinct holes — a deliberate placement
   * never leaves a lead floating.
   * Throws NOT_FOUND / INVALID_REF / ILLEGAL_PLACEMENT. Returns a copy.
   */
  movePartEnds(id, boardId, anchor, end) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    const def = partDef(comp.ref);
    if (!def?.rotatable) {
      throw taggedError(`${comp.ref} is not rotatable`, "INVALID_REF");
    }
    if (!this.#doc.boards.some((b) => b.id === boardId)) {
      throw taggedError(`no board ${boardId}`, "NOT_FOUND");
    }
    const params = normalizeParams(def, { ...comp.params, rot: 90, end });
    if (
      !this.canPlacePart(comp.ref, boardId, anchor, { ignoreId: id, params })
    ) {
      throw taggedError(
        `${id} cannot sit at ${boardId}.${anchor} + (${end?.dx},${end?.dy})`,
        "ILLEGAL_PLACEMENT",
      );
    }
    comp.board = boardId;
    comp.anchor = anchor;
    comp.params = params;
    return { ...comp };
  }

  /**
   * Rotate a placed part in situ. A rotatable two-lead part (resistor) turns
   * 90° around pin 1: pin 1 stays, pin 2's lead swings a quarter lap (tries CW
   * then CCW), stored in its two-free-ends form (`rot: 90`, `end` a `{dx, dy}`
   * bend). A rigid `def.can` part (an oscillator) spins about its own centre —
   * 180° per call for a non-square can (full-can), a plain 90° for a square one
   * (half-can); see the `def?.can` branch below for why.
   * Throws NOT_FOUND / INVALID_REF (not rotatable) / ILLEGAL_PLACEMENT (no free
   * hole at either rotated position). Returns a copy.
   */
  rotateComponent(id) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    const def = partDef(comp.ref);
    // A rigid multi-corner shape (an oscillator can) spins IN PLACE around its
    // own centre — pin 1 (the stored anchor) moves to a new hole each turn,
    // unlike the pivot-on-pin-1 rotate below. Recover the centre from the
    // current anchor + rot, then find the next rot's anchor near that same
    // centre. A non-square can (the full-can, 6×3) already covers both of its
    // distinct footprints in half a lap, so ROTATING AN ALREADY-SEATED ONE
    // jumps straight 180° (0↔180, 90↔270) rather than stopping at the
    // intermediate 90°/270° swapped-footprint orientation; the ghost while
    // PLACING still steps a full quarter-turn (`desk-controller.js`) so every
    // orientation stays reachable when hunting for a fit. A square can (the
    // half-can) keeps the plain quarter-turn either way.
    if (def?.can) {
      const board = this.#doc.boards.find((b) => b.id === comp.board);
      const anchorPos =
        board && holePosition(board.type, comp.anchor, board.rot ?? 0);
      if (!anchorPos) {
        throw taggedError(`${id} has no pins`, "ILLEGAL_PLACEMENT");
      }
      const anchorWorld = {
        x: board.x + anchorPos.x,
        y: board.y + anchorPos.y,
      };
      const curRot = comp.params?.rot ?? 0;
      const step = def.can.width !== def.can.height ? 2 : 1;
      const nextRot =
        ROTATIONS[(ROTATIONS.indexOf(curRot) + step) % ROTATIONS.length];
      const toCenter = rotateOffset(canCornerOffset(def), curRot);
      const center = {
        x: anchorWorld.x - toCenter.dx,
        y: anchorWorld.y - toCenter.dy,
      };
      const toAnchor = rotateOffset(canCornerOffset(def), nextRot);
      const candidate = addressAtWorld(
        this.#doc.boards,
        center.x + toAnchor.dx,
        center.y + toAnchor.dy,
      );
      const parsed = candidate && parseAddress(candidate);
      const params = normalizeParams(def, { ...comp.params, rot: nextRot });
      if (
        !parsed ||
        !this.canPlacePart(comp.ref, parsed.boardId, parsed.hole, {
          ignoreId: id,
          params,
        })
      ) {
        throw taggedError(`${id} cannot rotate here`, "ILLEGAL_PLACEMENT");
      }
      comp.board = parsed.boardId;
      comp.anchor = parsed.hole;
      comp.params = params;
      return { ...comp };
    }
    // A DIP chip turns a half lap in place: its footprint maps onto itself, so
    // the holes (and every occupancy check) are unchanged — only the pin
    // numbering reverses. Nothing can block it. A `reversible` linear discrete
    // (the bussed resistor array) is the same move on one row: evenly spaced
    // holes, so it covers exactly the nine it already covers and only the
    // numbering — hence which end is the common bus — turns round.
    // A rigid body that turns a quarter lap about pin 1 (`def.turns` — the
    // speaker, whose legs are the channel's width apart, so on end it stands
    // across the trench). R steps to the next quarter it can take, pin 1
    // staying in its hole; one with nowhere to go is stepped past.
    if (def?.turns) {
      const cur = ROTATIONS.includes(comp.params?.rot) ? comp.params.rot : 0;
      // A rigid body: its far leg must stand IN a hole, not merely near one
      // (from row a, three pitches down is 0.24 off a dovetailed rail's row)
      // — the quarter the placement ghost could not have shown is skipped.
      const exact = (params) => {
        const offsets = footprintOffsets(def, params);
        const want = offsets.map((dx) => rotateOffset({ dx, dy: 0 }, params.rot ?? 0)); // prettier-ignore
        const got = pinVectors(this.#doc, { ...comp, params });
        return Boolean(got) && want.every((v, i) => Math.hypot(got[i].dx - v.dx, got[i].dy - v.dy) < 0.005); // prettier-ignore
      };
      for (let step = 1; step < ROTATIONS.length; step++) {
        const rot = ROTATIONS[(ROTATIONS.indexOf(cur) + step) % ROTATIONS.length]; // prettier-ignore
        const params = normalizeParams(def, { ...comp.params, rot });
        const opts = { ignoreId: id, params };
        if (
          this.canPlacePart(comp.ref, comp.board, comp.anchor, opts) &&
          exact(params)
        ) {
          comp.params = params;
          return { ...comp };
        }
      }
      throw taggedError(`${id} cannot turn here`, "ILLEGAL_PLACEMENT");
    }
    // A TO-92 on BENT legs (free-legs.js) turns end for end as the straight
    // one does — the same holes, the pin numbering backwards — which bent is
    // pins 1 and 3 trading holes.
    if (legsBent(comp)) {
      const pins = partPinAddresses(this.#doc, comp);
      const one = pins?.find((p) => p.pin === 1)?.address;
      const three = pins?.find((p) => p.pin === 3)?.address;
      const seat =
        one && three
          ? legSeat(
              this.#doc,
              comp,
              new Map([
                [1, three],
                [3, one],
              ]),
            )
          : null;
      if (
        !seat ||
        !this.canPlacePart(comp.ref, seat.board, seat.anchor, {
          ignoreId: id,
          params: seat.params,
        })
      ) {
        throw taggedError(`${id} cannot turn here`, "ILLEGAL_PLACEMENT");
      }
      comp.board = seat.board;
      comp.anchor = seat.anchor;
      comp.params = seat.params;
      return { ...comp };
    }
    if (def?.package || def?.reversible) {
      comp.params = normalizeParams(def, {
        ...comp.params,
        rot: comp.params?.rot === 180 ? 0 : 180,
      });
      return { ...comp };
    }
    if (!def?.rotatable) {
      throw taggedError(`${comp.ref} is not rotatable`, "INVALID_REF");
    }
    const board = this.#doc.boards.find((b) => b.id === comp.board);
    const pins = board && partPinHoles(comp.ref, comp.anchor, comp.params);
    if (!pins) throw taggedError(`${id} has no pins`, "ILLEGAL_PLACEMENT");
    // The lead vector as it stands, whichever form the part is stored in: a
    // bend is already an offset; a footprint pair is the gap between its holes.
    let vec = pins[1].offset;
    if (!vec) {
      const p1 = holePosition(board.type, pins[0].hole);
      const p2 = holePosition(board.type, pins[1].hole);
      if (!p1 || !p2) {
        throw taggedError(`${id} has no pins`, "ILLEGAL_PLACEMENT");
      }
      vec = { dx: p2.x - p1.x, dy: p2.y - p1.y };
    }
    // Pivot pin 2 around pin 1 by ±90° (keep pin 1 fixed). Hole offsets are
    // integers, so the swung lead stays on the lattice and lands on a hole
    // whenever one is there — including a NEIGHBOURING strip's rail. Negating
    // a zero component yields -0, which would persist into the document and
    // break value comparisons, so fold it back.
    const swing = (n) => (n === 0 ? 0 : n);
    const fits = (end) => {
      // The swung lead is stored as the vector to the hole it lands in
      // (pinVectors), so it is drawn IN that hole and turns on from there.
      const swung = normalizeParams(def, { ...comp.params, rot: 90, end });
      const vectors = pinVectors(this.#doc, { ...comp, params: swung });
      if (!vectors) return null;
      const params = normalizeParams(def, {
        ...comp.params,
        rot: 90,
        end: vectors[1],
      });
      const ok = this.canPlacePart(comp.ref, comp.board, comp.anchor, {
        ignoreId: id,
        params,
      });
      return ok ? params : null;
    };
    // The lengths a turned lead may take: its footprint's own first, then the
    // nearest others — never shorter than its `minSpan` (a diode's three
    // holes), at most three pitches longer — for a part a pitch or two from
    // the trench or the board's edge, turned on end (Jason, 2026-10-14: a
    // diode in row c turned into the channel and sat still).
    const natural = footprintOffsets(def, comp.params).at(-1);
    const shortest = Math.max(1, Math.ceil(def.minSpan ?? 1));
    const lengths = [];
    for (let len = shortest; len <= natural + 3; len++) lengths.push(len);
    lengths.sort(
      (a, b) => Math.abs(a - natural) - Math.abs(b - natural) || a - b,
    );
    const reach = Math.hypot(vec.dx, vec.dy);
    // A quarter turn either way round — clockwise first — at its footprint's
    // length if either has a hole there, so a lead stretched to get round the
    // channel comes back to size on the next turn; then the nearest other
    // lengths; the length it has now last, for a bend no lattice length fits
    // (a lead reaching a rail).
    const turns = [1, -1].map((d) => ({
      dx: swing(-d * vec.dy) / reach,
      dy: swing(d * vec.dx) / reach,
    }));
    for (const len of [...lengths, reach]) {
      for (const t of turns) {
        const params = fits({ dx: swing(t.dx * len), dy: swing(t.dy * len) });
        if (params) {
          comp.params = params;
          return { ...comp };
        }
      }
    }
    throw taggedError(`${id} cannot rotate here`, "ILLEGAL_PLACEMENT");
  }

  /**
   * Update a component's params through the def's contract (switch position,
   * LED color/flip, PSU volts). Throws NOT_FOUND. Returns a copy.
   */
  setComponentParams(id, patch) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    comp.params = normalizeParams(partDef(comp.ref), {
      ...comp.params,
      ...patch,
    });
    return { ...comp };
  }

  /**
   * Whether `patch` may go onto a component's params where it sits — the
   * Properties dialog's question before it applies a change. Nearly always
   * yes: only a change that MOVES a pin (an inductor set to three holes
   * between its leads lands its second lead one hole further on) is asked
   * about, and then the new seat must be one `canPlacePart` would accept. A
   * change that moves nothing is never refused — not even on a part whose
   * lead is floating, which no seat check would pass.
   * @param {string} id
   * @param {object} patch
   * @returns {boolean}
   */
  canSetComponentParams(id, patch) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) return false;
    if (comp.board == null) return true; // a brick has terminals, not pins
    const params = normalizeParams(partDef(comp.ref), {
      ...comp.params,
      ...patch,
    });
    const before = partPinHoles(comp.ref, comp.anchor, comp.params);
    const after = partPinHoles(comp.ref, comp.anchor, params);
    if (JSON.stringify(before) === JSON.stringify(after)) return true;
    return this.canPlacePart(comp.ref, comp.board, comp.anchor, {
      ignoreId: id,
      params,
    });
  }

  /**
   * Swap a part for one of its siblings in place — a ceramic capacitor for an
   * electrolytic, an NPN for a MOSFET — as its Properties card's Type field
   * does. Only a part its def names in `swapsWith` qualifies, and those share
   * one footprint, so the holes, the wiring and every address stay exactly
   * as they were (checked, not assumed). The params are carried across
   * through the new def — its `adoptParams` first, where it has one (a
   * transistor drops a part number that names another type) — and nothing
   * else changes: a capacitor keeps its value even where the new type's
   * range does not hold it, and its card says so.
   * Throws NOT_FOUND / INVALID_REF (not a sibling) / ILLEGAL_PLACEMENT.
   * Returns a copy.
   */
  setComponentRef(id, ref) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    if (ref === comp.ref) return { ...comp };
    const def = partDef(comp.ref);
    const next = partDef(ref);
    if (!next || !def?.swapsWith?.includes(ref)) {
      throw taggedError(`${comp.ref} cannot become ${ref}`, "INVALID_REF");
    }
    const params = normalizeParams(
      next,
      next.adoptParams ? next.adoptParams(comp.params) : comp.params,
    );
    const before = partPinHoles(comp.ref, comp.anchor, comp.params);
    const after = partPinHoles(ref, comp.anchor, params);
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      throw taggedError(
        `${ref} does not fit ${id}'s holes`,
        "ILLEGAL_PLACEMENT",
      );
    }
    comp.ref = ref;
    comp.params = params;
    return { ...comp };
  }

  /**
   * Update a component's Name/Description — the shared Properties dialog's
   * universal metadata, kept OUTSIDE `params` so it never touches a def's own
   * normalizeParams contract (every part gets it, chips included, unlike
   * catalog-declared params). Present only when non-empty, same omit-when-empty
   * convention as setBoardParams/schematicPos. Throws NOT_FOUND. Returns a copy.
   */
  setComponentMeta(id, patch) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    if (typeof patch.name === "string") {
      if (patch.name) comp.name = patch.name;
      else delete comp.name;
    }
    if (typeof patch.description === "string") {
      if (patch.description) comp.description = patch.description;
      else delete comp.description;
    }
    return { ...comp };
  }

  /**
   * Set (or, with a non-finite coordinate, clear) a component's schematic-view
   * position nudge (Feature 150). A pure layout hint — the desk placement is
   * untouched — so a re-layout honours the user's arrangement. Throws
   * NOT_FOUND. Returns a copy of the component.
   */
  setSchematicPos(id, x, y) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (!comp) throw taggedError(`no component ${id}`, "NOT_FOUND");
    const pos = normalizeSchematicPos({ x, y });
    if (pos) comp.schematicPos = pos;
    else delete comp.schematicPos;
    return { ...comp };
  }

  /** Clear every schematic-view position nudge (a full auto-layout reset). */
  clearSchematicPositions() {
    let cleared = 0;
    for (const comp of this.#doc.components) {
      if (comp.schematicPos) {
        delete comp.schematicPos;
        cleared++;
      }
    }
    return cleared;
  }

  /**
   * Remove a component. A PSU takes its attached wires with it (terminals
   * would dangle otherwise). Throws NOT_FOUND.
   */
  removeComponent(id) {
    const i = this.#doc.components.findIndex((c) => c.id === id);
    if (i === -1) throw taggedError(`no component ${id}`, "NOT_FOUND");
    const [removed] = this.#doc.components.splice(i, 1);
    this.#detachAnnotations(id); // an anchored label falls free, keeping its spot
    if (removed.board == null) {
      // A desk-level brick (PSU, clock) takes its attached wires with it.
      this.#doc.wires = this.#doc.wires.filter(
        (w) => !this.#wireTouches(w, id),
      );
      this.#pruneBusesToWires();
      // …and a meter's probe on one of its terminals falls loose.
      this.#detachProbes(id, { boards: [], components: [removed] });
    }
  }

  /**
   * Every meter probe resting on an owner that is going away (a board's
   * holes, a brick's terminals) falls loose, lying where its tip was — a
   * probe with nowhere to rest is still the meter's. `owner` holds the
   * owner (already out of the document), so the tip is read off it.
   */
  #detachProbes(ownerId, owner) {
    for (const { meter, which } of probesOn(this.#doc, ownerId)) {
      const tip = pointWorld(owner, meter.params[which].at);
      meter.params[which] = tip ? lyingAt(meter, tip) : restingProbe(which);
    }
  }

  /** The meter probes resting on a board's holes: `[{meter, which, at, key}]`. */
  #probesInHoles() {
    const out = [];
    for (const meter of meters(this.#doc)) {
      for (const which of PROBES) {
        const at = meter.params?.[which]?.at;
        if (typeof at !== "string") continue;
        const boardId = parseAddress(at)?.boardId;
        if (!this.#doc.boards.some((b) => b.id === boardId)) continue;
        out.push({ meter, which, at, key: `${meter.id}|${which}|${at}` });
      }
    }
    return out;
  }

  /** Remember, for each probe in a hole, whether a lead is in it now — what
      pressProbesOff compares the next edit against. */
  #noteProbes() {
    this.#probeHoles = new Map();
    const probes = this.#probesInHoles();
    if (!probes.length) return;
    const occ = buildOccupancy(this.#doc);
    for (const { at, key } of probes) this.#probeHoles.set(key, occ.has(at));
  }

  /**
   * A probe resting in a hole is pushed out by what is pressed into it, as on
   * a bench: a part's BODY now standing over the hole (a 600-mil chip's rows
   * e–g, where no tip can reach — `canProbeAt` refuses it), or a LEAD now in a
   * hole that held none when the last edit ended (a pin, a wire end, a flag
   * or tag). The probe lies where its tip was, touching nothing. A probe put
   * on a hole that ALREADY holds a lead stays: it touches the lead. Run by
   * the desk at every edit, before the edit is recorded, so it is one undo
   * step with it (DeskController #emitDocChanged). Returns how many it lifted.
   */
  pressProbesOff() {
    const probes = this.#probesInHoles();
    if (!probes.length) {
      this.#probeHoles = new Map();
      return 0;
    }
    const occ = buildOccupancy(this.#doc);
    const seen = new Map();
    let lifted = 0;
    for (const { meter, which, at, key } of probes) {
      const claim = occ.get(at);
      const pushed =
        claim?.kind === "body" ||
        (claim != null && this.#probeHoles.get(key) === false);
      if (!pushed) {
        seen.set(key, claim != null);
        continue;
      }
      const tip = pointWorld(this.#doc, at);
      meter.params = {
        ...meter.params,
        [which]: tip ? lyingAt(meter, tip) : restingProbe(which),
      };
      lifted += 1;
    }
    this.#probeHoles = seen;
    return lifted;
  }

  // ── Multimeters (catalog/meter.js) ────────────────────────────────────────

  /**
   * Put one of a meter's probes on a point (`{at}` — any real hole or brick
   * terminal, taken or free: a probe is no lead) or lay it loose at a desk
   * point (`{x, y}`). Throws NOT_FOUND / INVALID_ARG. Returns a copy.
   */
  setMeterProbe(id, which, target) {
    const meter = this.#doc.components.find((c) => c.id === id && c.kind === "meter"); // prettier-ignore
    if (!meter) throw taggedError(`no meter ${id}`, "NOT_FOUND");
    if (!PROBES.includes(which)) {
      throw taggedError(`no probe ${which}`, "INVALID_ARG");
    }
    let probe;
    if (typeof target?.at === "string") {
      if (!this.canProbeAt(target.at)) {
        throw taggedError(`${target.at} is nothing to probe`, "INVALID_ARG");
      }
      probe = { at: target.at };
      // What is in the hole now is what the next edit is weighed against
      // (pressProbesOff): a lead already there is touched, not pressed in.
      this.#probeHoles.set(
        `${id}|${which}|${target.at}`,
        buildOccupancy(this.#doc).has(target.at),
      );
    } else if (Number.isFinite(target?.x) && Number.isFinite(target?.y)) {
      probe = lyingAt(meter, target);
    } else {
      throw taggedError("a probe goes on a point or the desk", "INVALID_ARG");
    }
    return this.setComponentParams(id, { [which]: probe });
  }

  /** Whether a probe may rest on `address`: a real hole or brick terminal,
      whatever is in it (a probe touches the lead there) — but not a hole
      under a 600-mil chip's BODY, which no tip can reach (a wire end is
      refused there too). `covered` (`coveredAddresses()`) is a gesture's
      hoisted copy of those holes; without it the hole's own strip is read. */
  canProbeAt(address, { covered = null } = {}) {
    if (!isRealPoint(this.#doc, address)) return false;
    if (covered) return !covered.has(address);
    const boardId = parseAddress(address)?.boardId;
    return !this.#doc.components.some(
      (c) =>
        c?.board === boardId &&
        (c.kind === "chip" || c.kind === "discrete") &&
        partCoverAddresses(this.#doc, c).includes(address),
    );
  }

  /** Every hole a part's body stands over, desk-wide (`canProbeAt`'s
      `covered`). */
  coveredAddresses() {
    const out = new Set();
    for (const c of this.#doc.components) {
      if (c?.kind !== "chip" && c?.kind !== "discrete") continue;
      for (const a of partCoverAddresses(this.#doc, c)) out.add(a);
    }
    return out;
  }

  /** What one component puts into board holes — its pins' and body's
      addresses (a meter: where its probes rest), as a comparable string; ""
      for a brick or a missing id. Equal before and after a params edit means
      the edit pressed nothing into a hole and lifted nothing out of one, so
      the desk may skip `pressProbesOff` for it (DeskController
      #emitDocChanged's `seatsKept`) — one part's worth of geometry instead
      of the whole desk's occupancy. */
  seatKey(id) {
    const comp = this.#doc.components.find((c) => c.id === id);
    if (comp?.kind === "meter") {
      return JSON.stringify(PROBES.map((w) => comp.params?.[w]?.at ?? null));
    }
    if (comp?.kind !== "chip" && comp?.kind !== "discrete") return "";
    return JSON.stringify([
      partPinAddresses(this.#doc, comp),
      partCoverAddresses(this.#doc, comp),
    ]);
  }

  // ── Desk-level bricks: PSU + clock (Feature 60 / 100) ─────────────────────

  /**
   * Drop a brick (`kind` ∈ psu | clock | load) on the desk, snapped to the lattice.
   * Throws INVALID_KIND / INVALID_ARG / OVERLAP. Returns a copy.
   */
  addBrick(kind, x, y, params = {}) {
    const brick = BRICKS[kind];
    if (!brick)
      throw taggedError(`unsupported brick kind: ${kind}`, "INVALID_KIND");
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw taggedError("brick position must be finite", "INVALID_ARG");
    }
    // A NEW brick lands on whole pitches both ways; check the rect it gets.
    if (!this.canPlaceBrick(kind, x, Math.round(y))) {
      throw taggedError(
        `a ${kind} at ${Math.round(x)},${Math.round(y)} covers a board or brick`,
        "OVERLAP",
      );
    }
    const seq = this.#doc[brick.counter] ?? 1;
    this.#doc[brick.counter] = seq + 1;
    // A new brick is never pre-damaged (a pasted meter's blown fuse), and a
    // new meter's probes lie beside it: pasted, what they touched is not
    // where it landed.
    const fresh = loadParams(partDef(kind), params);
    if (kind === "meter") {
      for (const which of PROBES) fresh[which] = restingProbe(which);
    }
    const component = {
      id: `${brick.prefix}${seq}`,
      kind,
      ref: kind,
      x: Math.round(x),
      y: Math.round(y),
      params: fresh,
    };
    this.#doc.components.push(component);
    return { ...component };
  }

  /** Drop a PSU brick (kind "psu"). */
  addPsu(x, y, params = {}) {
    return this.addBrick("psu", x, y, params);
  }

  /** Move a desk-level brick. Throws NOT_FOUND / INVALID_ARG / OVERLAP. */
  moveBrick(id, x, y) {
    const brick = this.#doc.components.find(
      (c) => c.id === id && c.board == null && BRICKS[c.kind],
    );
    if (!brick) throw taggedError(`no brick ${id}`, "NOT_FOUND");
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw taggedError("brick position must be finite", "INVALID_ARG");
    }
    if (!this.canPlaceBrick(brick.kind, x, y, { ignoreId: id })) {
      throw taggedError(
        `moving ${id} to ${Math.round(x)},${Math.round(y)} covers something`,
        "OVERLAP",
      );
    }
    // Whole pitches across; DOWN the 0.01 grid, as `translateAll` and the
    // loader keep a brick — rounding y here snapped a brick recentred onto a
    // fractional row by up to half a pitch on its next drag (the drag itself
    // moves it by whole pitches: DeskController#resolveBrickPos).
    brick.x = Math.round(x);
    brick.y = boardCoord(y);
    return { ...brick };
  }

  /** Move a PSU brick (back-compat name). */
  movePsu(id, x, y) {
    return this.moveBrick(id, x, y);
  }

  // ── Wires (Feature 50) ───────────────────────────────────────────────────

  /** Copies of the wires on the desk. */
  get wires() {
    return this.#doc.wires.map(copyWire);
  }

  /** A copy of one wire, or null. */
  getWire(id) {
    const w = this.#doc.wires.find((x) => x.id === id);
    return w ? copyWire(w) : null;
  }

  /** Copies of the wires with an endpoint on one owner (board or PSU). */
  wiresTouching(ownerId) {
    return this.#doc.wires
      .filter((w) => this.#wireTouches(w, ownerId))
      .map(copyWire);
  }

  /** Is `address` a real, unoccupied hole? (occupancy delegation) */
  /**
   * `isHoleFree` for MANY addresses against one snapshot of the desk: the
   * occupancy index is built once, here, and the returned predicate only
   * looks addresses up. For a caller that asks about a whole run of holes in
   * one go (the bus tool's hover — `width` holes at each end, twice) instead
   * of rebuilding the index per hole. Valid until the document changes.
   *
   * @returns {(address: string) => boolean}
   */
  freeHoles() {
    const occupied = buildOccupancy(this.#doc);
    return (address) =>
      isRealPoint(this.#doc, address) && !occupied.has(address);
  }

  isHoleFree(address) {
    return isFreeHole(this.#doc, address);
  }

  /** May a wire connect these holes? (occupancy delegation) */
  canPlaceWire(from, to) {
    return canPlaceWire(this.#doc, from, to);
  }

  /** May wire `id`'s `end` move to `address`? (occupancy delegation) */
  canReendWire(id, end, address, { occupancy = null } = {}) {
    return canReendWire(this.#doc, id, end, address, occupancy);
  }

  /** May wire `id` move rigidly to connect `from` → `to`? (occupancy) */
  canMoveWire(id, from, to, { occupancy = null } = {}) {
    return canMoveWire(this.#doc, id, from, to, occupancy);
  }

  /**
   * Connect two free holes. `layout` is the wire's drawing method (see
   * WIRE_LAYOUTS — "direct" unless the app-wide default says otherwise), and
   * `points` its waypoints, which only a routed wire keeps (a paste carries
   * a design's existing routing across). Throws INVALID_ARG (bad color or
   * layout) / ILLEGAL_PLACEMENT (either end unreal, occupied, or from === to).
   * Returns a copy of the new wire.
   */
  addWire({
    from,
    to,
    color = WIRE_COLORS[0],
    layout = "direct",
    points = [],
  }) {
    if (!WIRE_COLORS.includes(color)) {
      throw taggedError(`unknown wire color: ${color}`, "INVALID_ARG");
    }
    if (!WIRE_LAYOUTS.includes(layout)) {
      throw taggedError(`unknown wire layout: ${layout}`, "INVALID_ARG");
    }
    const occ = this.#batchOcc;
    const placeable = occ
      ? from !== to &&
        [from, to].every((a) => isRealPoint(this.#doc, a) && !occ.has(a))
      : canPlaceWire(this.#doc, from, to);
    if (!placeable) {
      throw taggedError(
        `a wire cannot connect ${from} → ${to}`,
        "ILLEGAL_PLACEMENT",
      );
    }
    const wire = { id: `w${this.#doc.nextWireId++}`, from, to, color };
    applyWireLayout(wire, layout, points);
    this.#doc.wires.push(wire);
    if (occ) {
      occ.set(from, { kind: "wire", wireId: wire.id, end: "from" });
      occ.set(to, { kind: "wire", wireId: wire.id, end: "to" });
    }
    return copyWire(wire);
  }

  /**
   * Re-address ONE end of a wire (drag-an-endpoint). `end` is "from" | "to".
   * Throws NOT_FOUND (no such wire) / INVALID_ARG (bad end) / ILLEGAL_PLACEMENT
   * (target unreal, occupied, or the wire's other end). Returns the updated wire.
   */
  setWireEndpoint(id, end, address) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (end !== "from" && end !== "to") {
      throw taggedError(`bad wire end: ${end}`, "INVALID_ARG");
    }
    if (!canReendWire(this.#doc, id, end, address)) {
      throw taggedError(
        `wire ${id} cannot re-end at ${address}`,
        "ILLEGAL_PLACEMENT",
      );
    }
    wire[end] = address;
    return copyWire(wire);
  }

  /**
   * Move BOTH ends of a wire at once (the drag-the-whole-wire gesture, which
   * translates it rigidly). Throws NOT_FOUND (no such wire) / ILLEGAL_PLACEMENT
   * (either target unreal, occupied, or the two coincide). Returns the wire.
   */
  moveWire(id, from, to) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (!canMoveWire(this.#doc, id, from, to)) {
      throw taggedError(
        `wire ${id} cannot move to ${from} → ${to}`,
        "ILLEGAL_PLACEMENT",
      );
    }
    wire.from = from;
    wire.to = to;
    return copyWire(wire);
  }

  /**
   * Would every move in `moves` (each `{ id, from, to }`) land legally when
   * the whole batch is applied AT ONCE — the rigid whole-bus drag? Legality is
   * judged against a doc with EVERY moving wire lifted out, so members may
   * shuffle among the holes they collectively vacate (a bus shifted by its own
   * pitch), and every target must be a real point, free of any non-moving
   * lead, and claimed by exactly one move.
   *
   * A bus end-handle drag calls this once PER CANDIDATE OFFSET of its
   * snap search (up to thousands of times per drop on a wide bus) — so
   * unlike a single wire's own `isFreeHole` check, this builds the reduced
   * doc's occupancy map ONCE up front and reuses it for every address,
   * instead of `isFreeHole` silently rebuilding the whole map from scratch
   * per address (an O(members) multiplier on top of an already-expensive
   * per-candidate cost that measurably mattered at bus width).
   *
   * A drag probing the SAME wire ids over and over should hoist that map out
   * of its search loop entirely — see `prepareWireBatchMove`, which this is
   * a one-shot wrapper around.
   */
  canMoveWiresBatch(moves) {
    return this.prepareWireBatchMove(moves.map((m) => m.id))(moves);
  }

  /**
   * `canMoveWiresBatch` with the expensive half hoisted out: the reduced doc
   * (every one of `wireIds` lifted out) and its occupancy map are built ONCE
   * here, and the returned `(moves) => boolean` reuses them for every
   * candidate batch over those same ids.
   *
   * This is what a live drag wants. `canMoveWiresBatch` rebuilds the whole
   * document's occupancy — deriving every chip's pin addresses — on each
   * call, and a bus drag's snap search calls it up to 25 times per
   * `pointermove` (`SNAP_RADIUS`'s rings) or thousands of times for an
   * end-handle's unbounded release search. Prepared once per GESTURE that
   * collapses to one build, which is what let the live preview keep up with
   * the cursor on a wide bus.
   *
   * Valid only while the document is unchanged — a gesture holds one of
   * these for its own duration and drops it at the drop, and the commit
   * itself still re-validates through `moveWiresBatch`.
   *
   * @param {string[]} wireIds - the wires the batches will move
   * @returns {(moves: {id:string, from:string, to:string}[]) => boolean}
   */
  prepareWireBatchMove(wireIds) {
    const ids = new Set(wireIds);
    const known = new Set(this.#doc.wires.map((w) => w.id));
    // A wire named twice, or one that isn't in the document, can never make a
    // legal batch — resolve that up front rather than per candidate.
    const usable =
      ids.size === wireIds.length && wireIds.every((id) => known.has(id));
    if (!usable) return () => false;
    // The doc as if the movers were gone — the holes they leave read free.
    // Planted flags and tags stay: each is a lead in its hole, and a hole
    // they hold must read taken, as it does to `canReendWire`.
    const reduced = {
      boards: this.#doc.boards,
      components: this.#doc.components,
      wires: this.#doc.wires.filter((w) => !ids.has(w.id)),
      signals: this.#doc.signals,
      integrations: this.#doc.integrations,
    };
    const occupied = buildOccupancy(reduced);
    return (moves) => {
      if (moves.length !== wireIds.length) return false;
      const moved = new Set();
      const claimed = new Set();
      for (const { id, from, to } of moves) {
        if (!ids.has(id) || moved.has(id)) return false; // a wire moved twice
        moved.add(id);
        if (from === to) return false;
        for (const address of [from, to]) {
          if (claimed.has(address)) return false; // two leads into one hole
          if (!isRealPoint(reduced, address) || occupied.has(address)) {
            return false;
          }
          claimed.add(address);
        }
      }
      return true;
    };
  }

  /**
   * Apply a batch of wire moves atomically (the whole-bus drag commit). Throws
   * ILLEGAL_PLACEMENT if the batch isn't collectively legal — nothing moves on
   * failure. Returns copies of the moved wires.
   */
  moveWiresBatch(moves) {
    if (!this.canMoveWiresBatch(moves)) {
      throw taggedError("wire batch move is illegal", "ILLEGAL_PLACEMENT");
    }
    const byId = new Map(this.#doc.wires.map((w) => [w.id, w]));
    const moved = [];
    for (const { id, from, to } of moves) {
      const wire = byId.get(id);
      wire.from = from;
      wire.to = to;
      moved.push({ ...wire });
    }
    return moved;
  }

  // ── Moving a whole SELECTION at once (the cluster drag) ──────────────────

  /**
   * The whole legality story for a cluster move, hoisted ONCE per gesture — the
   * sibling of `prepareWireBatchMove` above, and prepared for the same reason:
   * a live drag asks it per pointer sample, and `canPlacePart` rebuilds the
   * document's entire occupancy index on every call, which for N members would
   * be N rebuilds a frame.
   *
   * TWO DOCUMENTS, DELIBERATELY. Occupancy comes from the doc as if every
   * mover — components AND wires — were gone, because a member landing in a
   * hole one of its travelling companions is vacating is the ordinary case, not
   * a collision (this is exactly why `prepareWireBatchMove`, which lifts out
   * only the wires, cannot be reused here). But REALNESS is asked of the full
   * component list: a riding wire's far end may sit on a moving brick's
   * terminal (`psu1.+`), and a PSU does not stop existing because it is in the
   * air.
   *
   * ONE CLAIM SET DECIDES EVERYTHING ELSE. Every landing address — each moving
   * pin, both ends of each wire move — is claimed once and may be claimed once.
   * That is what catches a mover wanting a hole another mover wants, and a pin
   * landing on a rider that is STAYING PUT (which happens whenever a part slides
   * along its own column-half: its pins move a row, its riders don't move at
   * all). Note the riders' addresses are lifted out of `occupied`, so nothing
   * else would notice.
   *
   * Valid only while the document is unchanged — a gesture holds one for its own
   * duration, and `moveClusterWithWires` prepares a fresh one to commit through.
   *
   * Planted FLAGS (signal flags, Output/Input tags) ride a part as its wires
   * do: `flags` names the riding ones (`{kind, id, key?}`), lifted out like
   * every other mover, and each landing (`flagMoves`, `{kind, id, key?,
   * anchor}`) is one more claim — a board hole, free, claimed once.
   *
   * @param {{componentIds?: string[], wireIds?: string[], flags?: Array}} movers
   * @returns {(placements: Array, wireMoves?: Array, boardShift?: object,
   *   flagMoves?: Array) => boolean}
   *   placement: brick → `{id, x, y}`; board part → `{id, board, anchor}`
   */
  prepareClusterMove({ componentIds = [], wireIds = [], flags = [] } = {}) {
    const compIds = new Set(componentIds);
    const movingWires = new Set(wireIds);
    const flagKey = (f) => (f.kind === "tag" ? `t|${f.id}|${f.key}` : `s|${f.id}`); // prettier-ignore
    const movingFlags = new Set(flags.map(flagKey));
    const byId = new Map(this.#doc.components.map((c) => [c.id, c]));
    const knownWires = new Set(this.#doc.wires.map((w) => w.id));
    // A mover named twice, or one that isn't in the document, can never make a
    // legal batch — resolve that up front rather than per candidate.
    const usable =
      compIds.size === componentIds.length &&
      movingWires.size === wireIds.length &&
      componentIds.every((id) => byId.has(id)) &&
      wireIds.every((id) => knownWires.has(id)) &&
      movingFlags.size === flags.length;
    if (!usable) return () => false;

    // The flags and tags riding the move are lifted out with the rest; every
    // other one keeps its hole taken — one hole, one lead.
    const reduced = {
      boards: this.#doc.boards,
      components: this.#doc.components.filter((c) => !compIds.has(c.id)),
      wires: this.#doc.wires.filter((w) => !movingWires.has(w.id)),
      signals:
        movingFlags.size === 0
          ? this.#doc.signals
          : this.#doc.signals.map((s) =>
              movingFlags.has(flagKey({ kind: "signal", id: s.id }))
                ? { ...s, flag: undefined }
                : s,
            ),
      integrations:
        movingFlags.size === 0
          ? this.#doc.integrations
          : this.#doc.integrations.map((el) => ({
              ...el,
              tags: Object.fromEntries(
                Object.entries(el.tags ?? {}).filter(
                  ([key]) => !movingFlags.has(flagKey({ kind: "tag", id: el.id, key })), // prettier-ignore
                ),
              ),
            })),
    };
    const index = buildOccupancy(reduced);
    const real = { boards: this.#doc.boards, components: this.#doc.components };
    // The STAYING parts with a bent lead (or an oscillator can's corners):
    // resolved geometrically, such a lead re-lands when the strip under it
    // moves in the same gesture, and the index above still holds it at the
    // hole it is leaving. Read on the first check that moves strips (most
    // gestures move none): each one's addresses now, and where its free leads
    // are drawn — a lead nowhere near a moving strip, old place or new, cannot
    // re-land, so a busy desk's hundreds of resistors are not re-resolved per
    // sample.
    let stayingBent = null;
    const staying = () =>
      (stayingBent ??= reduced.components
        .filter(
          (c) =>
            (c?.kind === "chip" || c?.kind === "discrete") &&
            partPinHoles(c.ref, c.anchor, c.params)?.some((p) => p.offset),
        )
        .map((c) => ({
          comp: c,
          before: partPinAddresses(reduced, c) ?? [],
          leads: partPinsWorld(reduced.boards, c),
        })));
    // The index as it stands once the strips have moved: each staying lead
    // that re-lands vacates its old hole and claims its new one — so a mover
    // landing where such a lead is GOING is refused here, as the commit's own
    // board check (`canMoveBoardsBy`, run after the movers have moved) refuses
    // it. Most drags re-land nothing and get the prepared index back.
    const relandedIndex = (geo, ids) => {
      // Every strip moving re-lands nothing (see #bentLeadsCollide).
      if (reduced.boards.every((b) => ids.has(b.id))) return index;
      // Every moving strip's rect, where it is and where it goes, with a
      // pitch to spare (a hole's reach runs a little past its board's edge).
      const rects = [];
      for (const boards of [reduced.boards, geo.boards]) {
        for (const b of boards) {
          if (!ids.has(b.id)) continue;
          const r = outlineRect(b);
          rects.push({ x: r.x - 1, y: r.y - 1, width: r.width + 2, height: r.height + 2 }); // prettier-ignore
        }
      }
      const near = (p) => rects.some((r) => pointInRect(p, r));
      const landed = new Map();
      const vacated = new Set();
      for (const { comp, before, leads } of staying()) {
        if (!ids.has(comp.board) && leads && !leads.some(near)) continue;
        for (const [i, p] of (partPinAddresses(geo, comp) ?? []).entries()) {
          const was = before[i]?.address ?? null;
          if (p.address === was) continue;
          if (was != null) vacated.add(was);
          if (p.address != null) {
            landed.set(p.address, { kind: "pin", componentId: comp.id, pin: p.pin }); // prettier-ignore
          }
        }
      }
      if (landed.size === 0 && vacated.size === 0) return index;
      const get = (a) =>
        landed.has(a) ? landed.get(a) : vacated.has(a) ? undefined : index.get(a); // prettier-ignore
      return { get, has: (a) => get(a) !== undefined };
    };
    const movingBricks = new Set(
      [...compIds].filter((id) => byId.get(id).board == null),
    );
    // The leads that float NOW (their strip moved or deleted from under them):
    // a part carried by its other lead keeps such a one where it is, still in
    // mid-air — a state it was already in, not one the move puts it into.
    const floatingNow = new Map();
    for (const id of compIds) {
      const comp = byId.get(id);
      if (comp.board == null) continue;
      const loose = (partPinAddresses(real, comp) ?? [])
        .filter((p) => p.address == null)
        .map((p) => p.pin);
      if (loose.length) floatingNow.set(id, new Set(loose));
    }

    // `boardShift` ({ids, dx, dy}): the strips moving in the same gesture,
    // which a moving brick is checked against where they will be.
    return (placements, wireMoves = [], boardShift = null, flagMoves = []) => {
      if (placements.length !== componentIds.length) return false;
      // A part is judged where it will stand AMONG the strips as they will
      // stand: a lead plugged into a strip moving in the same gesture keeps
      // its address, which only the moved strip can say.
      const after = boardsAfterShift(reduced.boards, boardShift);
      const geo = after === reduced.boards ? reduced : { ...reduced, boards: after }; // prettier-ignore
      const occupied = geo === reduced ? index : relandedIndex(geo, boardShift.ids); // prettier-ignore
      if (wireMoves.length !== wireIds.length) return false;
      if ((flagMoves?.length ?? 0) !== flags.length) return false;
      const seen = new Set();
      const claimed = new Set();
      const bricks = []; // the rects the moving bricks are claiming
      for (const p of placements) {
        if (!compIds.has(p.id) || seen.has(p.id)) return false;
        seen.add(p.id);
        const comp = byId.get(p.id);
        if (comp.board == null) {
          // A brick claims desk AREA, not holes — the one thing the claim set
          // can't express, so its movers check each other by rect.
          const { width, height } = partDef(comp.ref).size;
          const rect = {
            x: Math.round(p.x),
            y: boardCoord(p.y),
            width,
            height,
          };
          if (bricks.some((r) => rectsOverlap(rect, r))) return false;
          if (
            !this.canPlaceBrick(comp.ref, p.x, p.y, {
              ignoreIds: movingBricks,
              boardShift,
            })
          ) {
            return false;
          }
          bricks.push(rect);
          continue;
        }
        if (p.board == null || p.anchor == null) return false;
        // A placement may bring its own PARAMS: a two-terminal part riding by
        // one leg bends, which is a change of form (and of the holes it lands
        // on), so the check has to judge the part as it will BE, not as it is.
        const seat = {
          ref: comp.ref,
          board: p.board,
          anchor: p.anchor,
          params: p.params ?? comp.params,
        };
        const floating = floatingNow.get(p.id) ?? null;
        if (!canPlacePart(geo, { ...seat, occupancy: occupied, floating })) {
          return false;
        }
        const pins = partPinAddresses(geo, seat);
        if (!pins) return false;
        for (const { pin, address } of pins) {
          if (address == null && floating?.has(pin)) continue;
          if (address == null || claimed.has(address)) return false;
          claimed.add(address);
        }
        // The holes its body will stand over are its too: no other mover's
        // pin, and no riding wire, may land under it.
        for (const address of partCoverAddresses(geo, seat)) {
          if (claimed.has(address)) return false;
          claimed.add(address);
        }
      }
      const moved = new Set();
      for (const { id, from, to } of wireMoves) {
        if (!movingWires.has(id) || moved.has(id)) return false;
        moved.add(id);
        if (from === to) return false;
        for (const address of [from, to]) {
          if (claimed.has(address)) return false; // two leads into one hole
          if (!isRealPoint(real, address) || occupied.has(address))
            return false;
          claimed.add(address);
        }
      }
      const planted = new Set();
      for (const f of flagMoves ?? []) {
        const key = flagKey(f);
        if (!movingFlags.has(key) || planted.has(key)) return false;
        planted.add(key);
        const address = f.anchor;
        if (claimed.has(address) || occupied.has(address)) return false;
        if (!this.isBoardHole(address)) return false; // a flag plugs into a board
        claimed.add(address);
      }
      return true;
    };
  }

  /**
   * Move a whole selection AND everything riding it — wires, and the leads of
   * the two-terminal parts attached to it — as ONE mutation, so ⌘Z restores the
   * group and its wiring together, because they were never several edits.
   *
   * It validates the WHOLE batch through `prepareClusterMove` and only then
   * writes, rather than replaying `moveComponent` / `moveBrick` /
   * `moveWiresBatch` member by member: each of those re-checks against the LIVE
   * document, so the first member to move lands on top of a sibling that hasn't
   * moved yet, and `moveWiresBatch`'s wires-only reduction would refuse a rider
   * heading for a hole a part is vacating. The snapshot still wraps the writes,
   * so a mutation added to this loop later can't leave half a move behind.
   *
   * Throws ILLEGAL_PLACEMENT (the batch isn't collectively legal — nothing
   * moves). Returns copies of what moved.
   *
   * @param {Array} placements - brick `{id, x, y}` / board part
   *   `{id, board, anchor, params?}` (params only when the part's FORM changes)
   * @param {{moves?:Array, points?:Array, boardShift?:object,
   *   carried?:object}} [plan] - from planClusterRiders; `boardShift`
   *   ({ids, dx, dy}) names the strips moving in the same gesture, which a
   *   moving brick is checked against where they will be
   *   (`prepareClusterMove`); `carried` ({points, dx, dy}) bends to shift by
   *   index (`wirePointsOverBoards`'s map)
   * @returns {{components: Array, wires: Array}}
   */
  moveClusterWithWires(placements, plan = null) {
    const wireMoves = plan?.moves ?? [];
    const flagMoves = plan?.flags ?? [];
    const check = this.prepareClusterMove({
      componentIds: placements.map((p) => p.id),
      wireIds: wireMoves.map((m) => m.id),
      flags: flagMoves,
    });
    if (!check(placements, wireMoves, plan?.boardShift ?? null, flagMoves)) {
      throw taggedError("cluster move is illegal", "ILLEGAL_PLACEMENT");
    }
    const before = this.snapshot();
    try {
      const byId = new Map(this.#doc.components.map((c) => [c.id, c]));
      const components = placements.map((p) => {
        const comp = byId.get(p.id);
        if (comp.board == null) {
          comp.x = Math.round(p.x);
          comp.y = boardCoord(p.y);
        } else {
          // Board and anchor, and PARAMS only when the placement brings them.
          // A rotatable member otherwise keeps whichever form it is stored in —
          // its bend is measured FROM the anchor, so a rigid translation needs
          // no rewrite (a SOLO body drag converts a rot-0 part to the
          // two-free-ends form; a group drag deliberately doesn't). A part
          // riding by ONE leg is the case that does bring them: it BENDS, and
          // only that form can say so.
          comp.board = p.board;
          comp.anchor = p.anchor;
          if (p.params) comp.params = normalizeParams(partDef(comp.ref), p.params); // prettier-ignore
        }
        return { ...comp };
      });
      const wireById = new Map(this.#doc.wires.map((w) => [w.id, w]));
      const wires = wireMoves.map(({ id, from, to }) => {
        const wire = wireById.get(id);
        wire.from = from;
        wire.to = to;
        return copyWire(wire);
      });
      for (const { id, dx, dy } of plan?.points ?? []) {
        for (const p of wireById.get(id)?.points ?? []) {
          p.x = wireCoord(p.x + dx);
          p.y = wireCoord(p.y + dy);
        }
      }
      // The planted flags and tags that rode, each to its landing hole (a
      // flag keeps its turn — `rot` is a desk rotation, not the part's).
      for (const f of flagMoves) {
        const target =
          f.kind === "tag"
            ? this.#doc.integrations.find((el) => el.id === f.id)?.tags?.[f.key]
            : this.#doc.signals.find((sig) => sig.id === f.id)?.flag;
        if (target) target.anchor = f.anchor;
      }
      // The bends a gesture with no strip in it carries by index (a wire
      // between two moving bricks) — with strips, moveBoardsBy carries them.
      const carry = plan?.carried;
      if (carry) this.#shiftCarriedPoints(carry.points, carry.dx, carry.dy);
      return { components, wires };
    } catch (err) {
      this.restore(before); // a refused move changes nothing at all
      throw err;
    }
  }

  /**
   * The selection as draggable members, in document order — or null when any id
   * fails to resolve, which is the press declining to start a drag at all.
   */
  clusterMembers(ids) {
    return clusterMembers(this.#doc, ids);
  }

  /** Where every member lands under one rigid world delta. */
  resolveClusterTargets(members, delta) {
    return resolveClusterTargets(this.#doc.boards, members, delta);
  }

  /**
   * The wires riding a whole selection, each end attributed to the member it
   * rides. Read once at pointerdown and frozen for the gesture; see
   * model/cluster-move.js.
   */
  wiresRidingCluster(ids) {
    return wiresRidingCluster(this.#doc, ids);
  }

  /** The two-terminal PARTS riding a whole selection, each riding lead
      attributed to the member it follows. Frozen with the wires. */
  partsRidingCluster(ids) {
    return partsRidingCluster(this.#doc, ids);
  }

  /** The planted flags and tags riding a whole selection. Frozen with the
      wires. */
  flagsRidingCluster(ids) {
    return flagsRidingCluster(this.#doc, ids);
  }

  /**
   * Where those riders land — `{ moves, points, parts, resolved }`. Pure
   * planning: a plan is not a legality verdict, and the caller still runs it
   * past a `prepareClusterMove` predicate before committing.
   */
  planClusterRiders(
    members,
    targets,
    riding,
    ridingParts,
    presetShifts,
    { ridingFlags = null, boardShift = null } = {},
  ) {
    return planClusterRiders(this.#doc, {
      members,
      targets,
      riding,
      ridingParts,
      ridingFlags,
      presetShifts,
      boardShift,
    });
  }

  /** `leadShift` (cluster-move.js) over this document — where a PICKED lead
      lands under a group delta. */
  leadShift(delta, moving, opts) {
    return leadShift(this.#doc, delta, moving, opts);
  }

  /** Change a wire's color. Throws NOT_FOUND / INVALID_ARG. */
  recolorWire(id, color) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (!WIRE_COLORS.includes(color)) {
      throw taggedError(`unknown wire color: ${color}`, "INVALID_ARG");
    }
    wire.color = color;
    return copyWire(wire);
  }

  /**
   * Update a wire's Name/Description — the shared Properties dialog's
   * universal metadata, kept separate from `recolorWire` (its one catalog-
   * style field, Color). Present only when non-empty, same omit-when-empty
   * convention as setBoardParams/setComponentMeta. Throws NOT_FOUND. Returns
   * a copy.
   */
  setWireMeta(id, patch) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (typeof patch.name === "string") {
      if (patch.name) wire.name = patch.name;
      else delete wire.name;
    }
    if (typeof patch.description === "string") {
      if (patch.description) wire.description = patch.description;
      else delete wire.description;
    }
    return copyWire(wire);
  }

  /**
   * Set a wire's layout method (see WIRE_LAYOUTS). Going back to "direct"
   * DROPS every waypoint: a direct wire is the curve between its two holes and
   * has nowhere to keep a bend, so keeping them would leave invisible state
   * that reappeared on a later switch. Throws NOT_FOUND / INVALID_ARG.
   * Returns a copy.
   */
  setWireLayout(id, layout) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (!WIRE_LAYOUTS.includes(layout)) {
      throw taggedError(`unknown wire layout: ${layout}`, "INVALID_ARG");
    }
    if (layout === "routed") {
      wire.layout = "routed";
    } else {
      delete wire.layout;
      delete wire.points;
    }
    return copyWire(wire);
  }

  /**
   * Replace a wire's WHOLE path in one go: switch it to routed and give it
   * `points` as its waypoints (an empty list is a routed wire drawn as the
   * straight run between its holes, which is a perfectly ordinary answer).
   *
   * This exists because the auto-router (Feature 360) produces a path, not a
   * sequence of bends. Building a twelve-corner route out of twelve
   * `addWirePoint` calls would be twelve validations of a shape that is only
   * meaningful whole, and — through `#emitDocChanged` — twelve chances for a
   * half-drawn wire to be observed.
   *
   * Throws NOT_FOUND / INVALID_ARG (a non-array, a non-finite coordinate, or
   * more than MAX_WIRE_POINTS points — the router is expected to have already
   * decided what to do about a route that long, so here it is an error rather
   * than a silent truncation). Returns a copy.
   */
  setWireRoute(id, points) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (!Array.isArray(points)) {
      throw taggedError("a route needs a list of points", "INVALID_ARG");
    }
    if (points.length > MAX_WIRE_POINTS) {
      throw taggedError(
        `a route may hold at most ${MAX_WIRE_POINTS} waypoints`,
        "INVALID_ARG",
      );
    }
    for (const p of points) {
      if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) {
        throw taggedError("a waypoint needs finite coordinates", "INVALID_ARG");
      }
    }
    wire.layout = "routed";
    // Omit-when-empty, the same convention removeWirePoint keeps, so a routed
    // wire with no bends round-trips through a file without an empty array.
    if (points.length === 0) delete wire.points;
    else wire.points = points.map((p) => ({ x: wireCoord(p.x), y: wireCoord(p.y) })); // prettier-ignore
    return copyWire(wire);
  }

  /**
   * Apply a whole routing plan — ONE mutation, and therefore one undo step.
   *
   * Every entry is written or none is: the try/restore is the same
   * snapshot-guarded transaction `pasteDesign` and `moveClusterWithWires` use,
   * and it matters for the same reason. A half-applied auto-route would leave
   * some wires orthogonal and some sagging with no single ⌘Z to undo it.
   *
   * An entry may also carry a **`from`** and/or **`to`** address: the router's
   * one address change, a power lead's rail end moved to a nearer hole on the
   * same node (`model/rail-reseat.js`). It rides in the same transaction
   * deliberately — the waypoints were computed for the new hole, so applying
   * one without the other would leave a route drawn to a hole its wire does not
   * end in, and a separate call would be a second undo step for one action.
   *
   * The endpoint goes first, so `setWireRoute` writes the path onto the wire the
   * user will actually see. Nothing here can change the netlist: a waypoint is a
   * drawing, and a moved end lands on a hole in the same electrical node — which
   * `rail-reseat.js` proves against the netlist before ever proposing it.
   *
   * @param {Array<{id:string, points:Array<{x,y}>, from?:string, to?:string}>} plan
   * @returns {number} how many wires were rerouted
   */
  applyRoutes(plan) {
    if (!Array.isArray(plan) || plan.length === 0) return 0;
    const before = this.snapshot();
    try {
      for (const entry of plan) {
        if (entry.from) this.setWireEndpoint(entry.id, "from", entry.from);
        if (entry.to) this.setWireEndpoint(entry.id, "to", entry.to);
        this.setWireRoute(entry.id, entry.points ?? []);
      }
      return plan.length;
    } catch (err) {
      this.restore(before); // a refused plan changes nothing at all
      throw err;
    }
  }

  /** The routed wire `id`, or a thrown NOT_FOUND / INVALID_ARG — the one place
      "a waypoint only exists on a routed wire" is enforced. */
  #routedWire(id) {
    const wire = this.#doc.wires.find((w) => w.id === id);
    if (!wire) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    if (wire.layout !== "routed") {
      throw taggedError(`wire ${id} is not routed`, "INVALID_ARG");
    }
    // Not given an empty `points` here: a call refused after this would leave
    // one behind, and a routed wire with no bends keeps none (omit-when-empty).
    return wire;
  }

  /**
   * Bend a routed wire: insert a waypoint at `index` (0 … points.length, the
   * index of the SEGMENT the user grabbed). Throws NOT_FOUND / INVALID_ARG
   * (not routed, bad index, unreal point) / ILLEGAL_PLACEMENT at
   * MAX_WIRE_POINTS. Returns a copy of the wire.
   */
  addWirePoint(id, index, point) {
    const wire = this.#routedWire(id);
    const count = wire.points?.length ?? 0;
    if (!Number.isInteger(index) || index < 0 || index > count) {
      throw taggedError(`bad waypoint index: ${index}`, "INVALID_ARG");
    }
    if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) {
      throw taggedError("a waypoint needs finite coordinates", "INVALID_ARG");
    }
    if (count >= MAX_WIRE_POINTS) {
      throw taggedError(
        `wire ${id} already has ${MAX_WIRE_POINTS} waypoints`,
        "ILLEGAL_PLACEMENT",
      );
    }
    wire.points ??= [];
    wire.points.splice(index, 0, { x: wireCoord(point.x), y: wireCoord(point.y) }); // prettier-ignore
    return copyWire(wire);
  }

  /** Move one existing waypoint. Throws NOT_FOUND / INVALID_ARG. Returns a
      copy of the wire. */
  moveWirePoint(id, index, point) {
    const wire = this.#routedWire(id);
    const count = wire.points?.length ?? 0;
    if (!Number.isInteger(index) || index < 0 || index >= count) {
      throw taggedError(`bad waypoint index: ${index}`, "INVALID_ARG");
    }
    if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) {
      throw taggedError("a waypoint needs finite coordinates", "INVALID_ARG");
    }
    wire.points[index] = { x: wireCoord(point.x), y: wireCoord(point.y) };
    return copyWire(wire);
  }

  /** Drop one waypoint (it was dragged onto a neighbour, or onto an end).
      Throws NOT_FOUND / INVALID_ARG. Returns a copy of the wire. */
  removeWirePoint(id, index) {
    const wire = this.#routedWire(id);
    const count = wire.points?.length ?? 0;
    if (!Number.isInteger(index) || index < 0 || index >= count) {
      throw taggedError(`bad waypoint index: ${index}`, "INVALID_ARG");
    }
    wire.points.splice(index, 1);
    if (wire.points.length === 0) delete wire.points; // omit-when-empty
    return copyWire(wire);
  }

  /** Remove a wire. A bus that included it simply shrinks. Throws NOT_FOUND. */
  removeWire(id) {
    const i = this.#doc.wires.findIndex((w) => w.id === id);
    if (i === -1) throw taggedError(`no wire ${id}`, "NOT_FOUND");
    this.#doc.wires.splice(i, 1);
    this.#pruneBusesToWires();
  }

  // ── Buses (Feature 130) ──────────────────────────────────────────────────
  // A bus is METADATA over wires: `{ id, name, width, color, members }`, where
  // each member is an ordinary wire that already lives in `doc.wires`. The
  // netlist, occupancy, and the engine never learn buses exist — they still see
  // N plain wires. This is the Feature 110 "strips stay in doc.boards" move
  // applied to wires. `width` comes from the name grammar (parseBusName).

  /** Copies of the buses on the desk (member lists copied too). */
  get buses() {
    return this.#doc.buses.map((b) => ({ ...b, members: [...b.members] }));
  }

  /** A copy of one bus, or null. */
  getBus(id) {
    const b = this.#doc.buses.find((x) => x.id === id);
    return b ? { ...b, members: [...b.members] } : null;
  }

  /** The bus a wire belongs to, or null. */
  busOfWire(wireId) {
    const b = this.#doc.buses.find((x) => x.members.includes(wireId));
    return b ? { ...b, members: [...b.members] } : null;
  }

  /**
   * Bundle existing wires into a bus. `name` sets the width/bit-order via the
   * grammar (`D[7:0]`); `memberIds` are wire ids in bit order (only surviving,
   * de-duplicated ones are kept). Throws INVALID_ARG (unparseable name / bad
   * color). Returns a copy of the new bus.
   */
  addBus(name, memberIds = [], { color = WIRE_COLORS[0] } = {}) {
    const parsed = parseBusName(name);
    if (!parsed) {
      throw taggedError(`bad bus name: ${name}`, "INVALID_ARG");
    }
    if (!WIRE_COLORS.includes(color)) {
      throw taggedError(`unknown bus color: ${color}`, "INVALID_ARG");
    }
    const seen = new Set();
    const members = [];
    for (const wid of memberIds) {
      if (this.#doc.wires.some((w) => w.id === wid) && !seen.has(wid)) {
        seen.add(wid);
        members.push(wid);
      }
    }
    const bus = {
      id: `bus${this.#doc.nextBusId++}`,
      name: name.trim(),
      width: Math.max(parsed.width, members.length, 1),
      color,
      members,
    };
    this.#doc.buses.push(bus);
    return { ...bus, members: [...bus.members] };
  }

  /**
   * Patch a bus's `name` (re-derives width), `color`, or `members`. Throws
   * NOT_FOUND / INVALID_ARG. Returns a copy.
   */
  updateBus(id, patch = {}) {
    const bus = this.#doc.buses.find((b) => b.id === id);
    if (!bus) throw taggedError(`no bus ${id}`, "NOT_FOUND");
    // Every field is checked before any is written: a patch refused for its
    // colour must not leave the bus renamed.
    const parsed = "name" in patch ? parseBusName(patch.name) : null;
    if ("name" in patch && !parsed) {
      throw taggedError(`bad bus name: ${patch.name}`, "INVALID_ARG");
    }
    if ("color" in patch && !WIRE_COLORS.includes(patch.color)) {
      throw taggedError(`unknown bus color: ${patch.color}`, "INVALID_ARG");
    }
    let declaredWidth = null;
    if ("name" in patch) {
      bus.name = patch.name.trim();
      declaredWidth = parsed.width;
    }
    if ("color" in patch) bus.color = patch.color;
    if ("members" in patch) {
      const seen = new Set();
      const members = [];
      for (const wid of patch.members ?? []) {
        if (this.#doc.wires.some((w) => w.id === wid) && !seen.has(wid)) {
          seen.add(wid);
          members.push(wid);
        }
      }
      bus.members = members;
    }
    // Width always reflects the FINAL name + FINAL members TOGETHER — never a
    // stale mix of one patched field's new value with the other's pre-patch
    // one (patching both in a single call used to do exactly that).
    if ("name" in patch || "members" in patch) {
      if (declaredWidth == null) declaredWidth = parseBusName(bus.name).width;
      bus.width = Math.max(declaredWidth, bus.members.length, 1);
    }
    return { ...bus, members: [...bus.members] };
  }

  /**
   * Remove a bus. With `cascadeWires`, its member wires go too (delete);
   * otherwise the wires stay and simply un-bundle. Throws NOT_FOUND.
   */
  removeBus(id, { cascadeWires = false } = {}) {
    const i = this.#doc.buses.findIndex((b) => b.id === id);
    if (i === -1) throw taggedError(`no bus ${id}`, "NOT_FOUND");
    const [removed] = this.#doc.buses.splice(i, 1);
    if (cascadeWires) {
      const drop = new Set(removed.members);
      this.#doc.wires = this.#doc.wires.filter((w) => !drop.has(w.id));
      this.#pruneBusesToWires(); // a shared wire (there shouldn't be) stays sane
    }
  }

  /** Drop from every bus any member wire that no longer exists — and a bus
      left with NO member at all (its wires deleted, or its board) goes with
      them: it would stay in the document for good, offered by the
      analyzer's add-channel menu as a bus with nothing in it. */
  #pruneBusesToWires() {
    const live = new Set(this.#doc.wires.map((w) => w.id));
    this.#doc.buses = this.#doc.buses.filter((bus) => {
      const had = bus.members.length;
      bus.members = bus.members.filter((wid) => live.has(wid));
      return bus.members.length > 0 || had === 0;
    });
  }

  // ── Net names (Feature 120) ──────────────────────────────────────────────
  // A NAME binds to a member ADDRESS, never the derived net key — the netlist
  // resolves each binding to its current net on every rebuild, so the name
  // survives edits that renumber the key. Metadata only: the engine, netlist
  // partitioning, and occupancy stay unaware of names.

  /** Copies of the net-name bindings on the desk. */
  get netNames() {
    return this.#doc.netNames.map((n) => ({ ...n }));
  }

  /** The name bound to an address, or null. */
  netNameAt(address) {
    return this.#doc.netNames.find((n) => n.address === address)?.name ?? null;
  }

  /**
   * Bind a name to the net that `address` sits on (upsert by address). The
   * address must parse and the name be a non-empty string. Throws INVALID_ARG.
   * Returns a copy of the binding.
   */
  nameNet(address, name) {
    if (!parseAddress(address)) {
      throw taggedError(`bad net address: ${address}`, "INVALID_ARG");
    }
    const clean = typeof name === "string" ? name.trim() : "";
    if (!clean) {
      throw taggedError("net name must be a non-empty string", "INVALID_ARG");
    }
    const existing = this.#doc.netNames.find((n) => n.address === address);
    if (existing) existing.name = clean;
    else this.#doc.netNames.push({ address, name: clean });
    return { address, name: clean };
  }

  /** Remove the name binding on `address`. @returns {boolean} true if removed. */
  clearNetName(address) {
    const before = this.#doc.netNames.length;
    this.#doc.netNames = this.#doc.netNames.filter(
      (n) => n.address !== address,
    );
    return this.#doc.netNames.length !== before;
  }

  // ── Annotations: labels & notes (Feature 120) ────────────────────────────
  // Pure desk decoration — pointer-selectable, draggable, and ignored by
  // occupancy, the netlist, and the engine. x/y are absolute world pitch
  // coordinates; an `anchor` (component id) makes the annotation ride that
  // part's moves (the caller shifts x/y by the same delta). Ids are `an<n>`.

  /** Copies of the annotations on the desk. */
  get annotations() {
    return this.#doc.annotations.map((a) => ({ ...a }));
  }

  /** A copy of one annotation, or null. */
  getAnnotation(id) {
    const a = this.#doc.annotations.find((x) => x.id === id);
    return a ? { ...a } : null;
  }

  /**
   * Add a label / note at (x, y) in world pitch units. `extra` may carry
   * `color` and `anchor`. Throws INVALID_KIND / INVALID_ARG. Returns a copy.
   */
  addAnnotation(kind, x, y, text = "", { color = null, anchor = null } = {}) {
    if (!ANNOTATION_KINDS.has(kind)) {
      throw taggedError(`unsupported annotation kind: ${kind}`, "INVALID_KIND");
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw taggedError("annotation position must be finite", "INVALID_ARG");
    }
    const ann = {
      id: `an${this.#doc.nextAnnotationId++}`,
      kind,
      x,
      y,
      text: typeof text === "string" ? text : "",
    };
    if (color) ann.color = color;
    if (anchor) ann.anchor = anchor;
    this.#doc.annotations.push(ann);
    return { ...ann };
  }

  /**
   * Patch an annotation's `x`, `y`, `text`, `color`, or `anchor` (a null/empty
   * color/anchor clears it). Throws NOT_FOUND. Returns a copy.
   */
  updateAnnotation(id, patch = {}) {
    const ann = this.#doc.annotations.find((a) => a.id === id);
    if (!ann) throw taggedError(`no annotation ${id}`, "NOT_FOUND");
    if (Number.isFinite(patch.x)) ann.x = patch.x;
    if (Number.isFinite(patch.y)) ann.y = patch.y;
    if (typeof patch.text === "string") ann.text = patch.text;
    if ("color" in patch) {
      if (patch.color) ann.color = patch.color;
      else delete ann.color;
    }
    if ("anchor" in patch) {
      if (patch.anchor) ann.anchor = patch.anchor;
      else delete ann.anchor;
    }
    return { ...ann };
  }

  /** Remove an annotation. Throws NOT_FOUND. */
  removeAnnotation(id) {
    const i = this.#doc.annotations.findIndex((a) => a.id === id);
    if (i === -1) throw taggedError(`no annotation ${id}`, "NOT_FOUND");
    this.#doc.annotations.splice(i, 1);
  }

  /** Detach any annotation anchored to a component that is going away. */
  #detachAnnotations(componentId) {
    for (const a of this.#doc.annotations) {
      if (a.anchor === componentId) delete a.anchor;
    }
  }

  // ── External signals: bench stimulus (Feature 370) ─────────────────────────
  // A signal is a BUTTON pinned to the viewport's right edge plus a FLAG whose
  // point plugs into one board hole. It is neither a component nor decoration:
  // occupancy claims the flag's hole and the engine drives its net, but it has
  // no footprint, no catalog def and no desk coordinates of its own. Rail order
  // IS document order, which is why "the next free slot" is simply an append.
  //
  // The cap is `MAX_SIGNALS` — one signal per digit key — and the KEY is what
  // ties a flag to its button; colours cycle and may repeat (model/signals.js).

  /** Copies of the signals, in rail order. */
  get signals() {
    return this.#doc.signals.map((s) => ({
      ...s,
      ...(s.flag ? { flag: { ...s.flag } } : {}),
    }));
  }

  /** A copy of one signal, or null. */
  getSignal(id) {
    const sig = this.#doc.signals.find((s) => s.id === id);
    return sig
      ? { ...sig, ...(sig.flag ? { flag: { ...sig.flag } } : {}) }
      : null;
  }

  /**
   * Add a signal, taking the next colour in the cycle (`nextSignalColor`).
   * Throws SIGNALS_FULL once every key has a signal. Returns a copy.
   */
  addSignal({ type, rest, name } = {}) {
    if (this.#doc.signals.length >= MAX_SIGNALS) {
      throw taggedError(`at most ${MAX_SIGNALS} signals`, "SIGNALS_FULL");
    }
    // The colour least in use across the signals AND the Output/Input cards
    // — the same pool a new card picks from (addIntegration), so a new signal
    // does not take the colour a card on the rail already wears.
    const color = nextSignalColor([
      ...this.#doc.signals,
      ...this.#doc.integrations,
    ]);
    const sig = {
      id: `sig${this.#doc.nextSignalId++}`,
      ...normalizeSignalFields({ color, type, rest }),
    };
    if (typeof name === "string" && name) sig.name = name;
    this.#doc.signals.push(sig);
    return { ...sig };
  }

  /**
   * Patch a signal's `color` / `type` / `rest`. Any signal colour is allowed,
   * one another signal holds included. Throws NOT_FOUND or INVALID_ARG.
   * Returns a copy.
   */
  updateSignal(id, patch = {}) {
    const sig = this.#doc.signals.find((s) => s.id === id);
    if (!sig) throw taggedError(`no signal ${id}`, "NOT_FOUND");
    // Judged whole, then written: a patch refused for one field must leave
    // the signal as it was, not half changed.
    const next = { ...sig };
    if (patch.color !== undefined) {
      if (!SIGNAL_COLORS.includes(patch.color)) {
        throw taggedError(`bad signal color: ${patch.color}`, "INVALID_ARG");
      }
      next.color = patch.color;
    }
    for (const key of ["type", "rest"]) {
      if (patch[key] === undefined) continue;
      const fields = normalizeSignalFields({ ...next, [key]: patch[key] });
      if (fields[key] !== patch[key]) {
        throw taggedError(`bad signal ${key}: ${patch[key]}`, "INVALID_ARG");
      }
      next[key] = patch[key];
    }
    for (const key of ["color", "type", "rest"]) sig[key] = next[key];
    return { ...sig, ...(sig.flag ? { flag: { ...sig.flag } } : {}) };
  }

  /** Update a signal's Name/Description — the setComponentMeta shape. */
  setSignalMeta(id, patch) {
    const sig = this.#doc.signals.find((s) => s.id === id);
    if (!sig) throw taggedError(`no signal ${id}`, "NOT_FOUND");
    for (const key of ["name", "description"]) {
      if (typeof patch[key] !== "string") continue;
      if (patch[key]) sig[key] = patch[key];
      else delete sig[key];
    }
    return { ...sig, ...(sig.flag ? { flag: { ...sig.flag } } : {}) };
  }

  /**
   * Plug a signal's flag into a board hole — the one method behind BOTH
   * planting an unplaced flag and moving a planted one, since `canPlaceFlag`
   * ignores this signal's own claim. Throws NOT_FOUND / NOT_A_POINT /
   * HOLE_TAKEN. Returns a copy.
   */
  plantSignalFlag(id, address, rot = 0) {
    const sig = this.#doc.signals.find((s) => s.id === id);
    if (!sig) throw taggedError(`no signal ${id}`, "NOT_FOUND");
    if (!this.isBoardHole(address)) {
      throw taggedError(`${address} is not a board hole`, "NOT_A_POINT");
    }
    if (!canPlaceFlag(this.#doc, id, address)) {
      throw taggedError(`${address} is already taken`, "HOLE_TAKEN");
    }
    sig.flag = { anchor: address, rot: normalizeFlagRotation(rot) };
    return { ...sig, flag: { ...sig.flag } };
  }

  /** Turn a planted flag a quarter-turn about its POINT (the anchor is never
      part of the sum, so there is nothing to recompute). Throws NOT_FOUND. */
  rotateSignalFlag(id) {
    const sig = this.#doc.signals.find((s) => s.id === id);
    if (!sig) throw taggedError(`no signal ${id}`, "NOT_FOUND");
    if (!sig.flag) return { ...sig };
    sig.flag.rot = nextFlagRotation(sig.flag.rot);
    return { ...sig, flag: { ...sig.flag } };
  }

  /** Unplug a flag — the signal survives, its button back on the rail. */
  unplantSignalFlag(id) {
    const sig = this.#doc.signals.find((s) => s.id === id);
    if (!sig) throw taggedError(`no signal ${id}`, "NOT_FOUND");
    delete sig.flag;
    return { ...sig };
  }

  /** Remove a signal outright — button, flag and all. Throws NOT_FOUND. */
  removeSignal(id) {
    const i = this.#doc.signals.findIndex((s) => s.id === id);
    if (i === -1) throw taggedError(`no signal ${id}`, "NOT_FOUND");
    this.#doc.signals.splice(i, 1);
  }

  /**
   * Move a signal to place `index` on the rail (0 = top). Its KEY is its
   * place (`signalKey`), so this renumbers every button between the two.
   * Throws NOT_FOUND / INVALID_ARG; returns whether anything moved.
   */
  moveSignal(id, index) {
    return moveWithin(this.#doc.signals, id, index, "signal");
  }

  /** Is this address a real hole on a real BOARD (not a component terminal —
      a flag plugs into a breadboard, not into a PSU's screw terminal)? */
  isBoardHole(address) {
    if (typeof address !== "string") return false;
    const parsed = parseAddress(address);
    if (!parsed) return false;
    const board = this.#doc.boards.find((b) => b.id === parsed.boardId);
    return Boolean(board) && parseHole(board.type, parsed.hole) !== null;
  }

  /** May this signal's flag plug in here? Free, real, ignoring its own claim. */
  canPlaceSignalFlag(id, address) {
    return this.isBoardHole(address) && canPlaceFlag(this.#doc, id, address);
  }

  /** Detach every flag planted on a board that is going away — THE RULE: a
      flag with nowhere to go stops existing; the signal never does. */
  #detachSignalFlags(boardId) {
    for (const sig of this.#doc.signals) {
      const parsed = sig.flag ? parseAddress(sig.flag.anchor) : null;
      if (parsed?.boardId === boardId) delete sig.flag;
    }
  }

  // ── Serial integration: Output and Input elements ─────────────────────────
  // An element is a CARD on the viewport rail (under the signal buttons) plus
  // TAGS — one per pin and one trigger — whose points plug into board holes.
  // Like a signal it is neither a component nor decoration: occupancy claims
  // every planted tag's hole and the engine drives an Input's pin nets, but it
  // has no footprint, no catalog def and no desk coordinates of its own. Rail
  // order IS document order, and so is the element's index on the wire among
  // its connection's elements of the same kind (model/integration.js).

  /** Copies of the elements, in rail order. */
  get integrations() {
    return this.#doc.integrations.map(copyElement);
  }

  /** A copy of one element, or null. */
  getIntegration(id) {
    const e = this.#doc.integrations.find((x) => x.id === id);
    return e ? copyElement(e) : null;
  }

  /**
   * Add an Output or Input, `width` pins wide (a drop-time preset — see
   * `presetFields`), or a data bus (always a byte, every pin Z), taking the
   * next colour in the cycle the signals share. Throws INTEGRATIONS_FULL past
   * `MAX_ELEMENTS` Outputs/Inputs or `MAX_DATA` data buses (each counts only
   * its own), INVALID_ARG for an unknown kind. Returns a copy.
   */
  addIntegration({ kind, width = 1, connection = null, name } = {}) {
    if (!ELEMENT_KINDS.includes(kind)) {
      throw taggedError(`bad element kind: ${kind}`, "INVALID_ARG");
    }
    if (elementsFull(this.#doc.integrations, kind)) {
      const max = kind === "data" ? MAX_DATA : MAX_ELEMENTS;
      throw taggedError(`at most ${max} of kind ${kind}`, "INTEGRATIONS_FULL");
    }
    const counter = ID_COUNTER[kind];
    const all = [...this.#doc.signals, ...this.#doc.integrations];
    // A data bus's colour is its own; everything else shares the cycle.
    const color = kind === "data" ? nextDataColor(all) : nextSignalColor(all);
    const element = {
      id: `${ID_PREFIX[kind]}${this.#doc[counter]++}`,
      kind,
      ...normalizeElementFields({
        kind,
        color,
        connection,
        fields: presetFields(width),
      }),
    };
    if (typeof name === "string" && name) element.name = name;
    this.#doc.integrations.push(element);
    return copyElement(element);
  }

  /**
   * Patch an element's `color` / `connection` / `triggerEdge` /
   * `triggerInit`. Throws NOT_FOUND or INVALID_ARG. Returns a copy.
   *
   * Moving the trigger ONTO Auto takes the trigger tag off the board and
   * PARKS it (`parkedTrigger`); moving it off Auto puts it back in the hole it
   * left — if that hole is still there and still free, and otherwise onto the
   * card (THE RULE: the tag waits, the element is none the worse). Switching
   * between two edges leaves the tag where it is.
   */
  updateIntegration(id, patch = {}) {
    const e = this.#element(id);
    if (patch.color !== undefined && !SIGNAL_COLORS.includes(patch.color)) {
      throw taggedError(`bad element color: ${patch.color}`, "INVALID_ARG");
    }
    if (
      e.kind === "data" &&
      patch.color !== undefined &&
      !freeDataColors(this.#doc.integrations, e.id).includes(patch.color)
    ) {
      throw taggedError(`${patch.color} is another data bus's`, "INVALID_ARG");
    }
    const next = normalizeElementFields({ ...e, ...patch });
    for (const key of ["triggerEdge", "triggerInit"]) {
      if (patch[key] !== undefined && next[key] !== patch[key]) {
        throw taggedError(`bad element ${key}: ${patch[key]}`, "INVALID_ARG");
      }
    }
    const wasAuto = isAutoTrigger(e);
    for (const key of ["color", "connection", "triggerEdge", "triggerInit"]) {
      if (patch[key] !== undefined) e[key] = next[key];
    }
    if (isAutoTrigger(e) && !wasAuto) this.#parkTrigger(e);
    if (!isAutoTrigger(e) && wasAuto) this.#unparkTrigger(e);
    return copyElement(e);
  }

  /** Onto Auto: the trigger tag leaves its hole, remembered but claiming
      nothing — the hole is free for anything while the element is on Auto. */
  #parkTrigger(e) {
    delete e.parkedTrigger;
    const tag = e.tags?.[TRIGGER_KEY];
    if (!tag) return;
    e.parkedTrigger = { ...tag };
    delete e.tags[TRIGGER_KEY];
    if (!Object.keys(e.tags).length) delete e.tags;
  }

  /** Off Auto: the parked trigger goes back where it was, when it can. */
  #unparkTrigger(e) {
    const park = e.parkedTrigger;
    delete e.parkedTrigger;
    if (!park) return;
    if (
      this.isBoardHole(park.anchor) &&
      canPlaceTag(this.#doc, e.id, TRIGGER_KEY, park.anchor)
    ) {
      e.tags = { ...(e.tags ?? {}), [TRIGGER_KEY]: { ...park } };
    }
  }

  /**
   * Re-shape an element's pins (Properties ▸ Pins). Tags are numbered by PIN,
   * so a re-shape leaves every planted tag in its hole — except those past
   * the new last pin, which have no pin left to be and are unplugged (THE
   * RULE again: the tag goes, the element stays). Returns a copy.
   */
  setIntegrationFields(id, fields) {
    const e = this.#element(id);
    if (e.kind === "data") {
      throw taggedError(`a data bus is always one byte: ${id}`, "INVALID_ARG");
    }
    e.fields = normalizeFields(fields);
    if (e.tags) {
      for (const key of Object.keys(e.tags)) {
        if (!hasTagKey(e, key)) delete e.tags[key];
      }
      if (!Object.keys(e.tags).length) delete e.tags;
    }
    return copyElement(e);
  }

  /** Update an element's Name/Description — the setComponentMeta shape. */
  setIntegrationMeta(id, patch) {
    const e = this.#element(id);
    for (const key of ["name", "description"]) {
      if (typeof patch[key] !== "string") continue;
      if (patch[key]) e[key] = patch[key];
      else delete e[key];
    }
    return copyElement(e);
  }

  /**
   * Plug one of an element's tags into a board hole — planting and moving are
   * the one method, as for a signal flag. Throws NOT_FOUND (element or tag
   * key) / NOT_A_POINT / HOLE_TAKEN. Returns a copy.
   */
  plantIntegrationTag(id, key, address, rot = 0) {
    const e = this.#element(id);
    if (!hasTagKey(e, key))
      throw taggedError(`no tag ${id}:${key}`, "NOT_FOUND");
    if (!this.isBoardHole(address)) {
      throw taggedError(`${address} is not a board hole`, "NOT_A_POINT");
    }
    if (!canPlaceTag(this.#doc, id, key, address)) {
      throw taggedError(`${address} is already taken`, "HOLE_TAKEN");
    }
    e.tags = { ...(e.tags ?? {}), [key]: { anchor: address, rot: normalizeTagRotation(rot) } }; // prettier-ignore
    return copyElement(e);
  }

  /** Turn a planted tag a quarter-turn about its point. */
  rotateIntegrationTag(id, key) {
    const e = this.#element(id);
    const tag = e.tags?.[key];
    if (tag) tag.rot = nextTagRotation(tag.rot);
    return copyElement(e);
  }

  /** Unplug one tag — back onto its card on the rail. A data pin goes back
      to Z, so it never arrives on the board again already driving. */
  unplantIntegrationTag(id, key) {
    const e = this.#element(id);
    if (e.tags) {
      delete e.tags[key];
      if (!Object.keys(e.tags).length) delete e.tags;
    }
    releaseDataPin(e, key);
    return copyElement(e);
  }

  /** Unplug every tag an element has — a parked trigger included, so a
      later switch off Auto cannot bring back a tag the user cleared. */
  unplantIntegrationTags(id) {
    const e = this.#element(id);
    delete e.tags;
    delete e.parkedTrigger;
    if (e.drive) e.drive = e.drive.map(() => "z");
    return copyElement(e);
  }

  /**
   * Set the level a data bus's pin drives — `"z"`, `"0"` or `"1"`. Only a
   * PLANTED pin has one to set (an unplugged pin is Z by rule). Throws
   * NOT_FOUND (no such data bus, or a pin it does not have / has not
   * planted) or INVALID_ARG (a level that is none of the three). Returns a
   * copy.
   */
  setDataPin(id, key, level) {
    const e = this.#element(id);
    if (e.kind !== "data" || !hasTagKey(e, key) || !isPinKey(key)) {
      throw taggedError(`no data pin ${id}:${key}`, "NOT_FOUND");
    }
    if (!DATA_LEVELS.includes(level)) {
      throw taggedError(`bad data level: ${level}`, "INVALID_ARG");
    }
    if (!e.tags?.[key]) {
      throw taggedError(`${id}:${key} is not planted`, "NOT_FOUND");
    }
    e.drive[Number(key) - 1] = level;
    return copyElement(e);
  }

  /** Remove an element outright — card, tags and all. Throws NOT_FOUND. */
  removeIntegration(id) {
    const i = this.#doc.integrations.findIndex((x) => x.id === id);
    if (i === -1) throw taggedError(`no element ${id}`, "NOT_FOUND");
    this.#doc.integrations.splice(i, 1);
  }

  /**
   * Move an element's card to place `index` among the cards. The rail's
   * order only: wire order (`elementsFor`) is by id, so no sketch, header or
   * layout signature changes. Throws NOT_FOUND / INVALID_ARG; returns whether
   * anything moved.
   */
  moveIntegration(id, index) {
    return moveWithin(this.#doc.integrations, id, index, "element");
  }

  /** May this tag plug in here? A real board hole, free, ignoring its own claim. */
  canPlaceIntegrationTag(id, key, address) {
    return (
      this.isBoardHole(address) && canPlaceTag(this.#doc, id, key, address)
    );
  }

  #element(id) {
    const e = this.#doc.integrations.find((x) => x.id === id);
    if (!e) throw taggedError(`no element ${id}`, "NOT_FOUND");
    return e;
  }

  /** Detach every tag planted on a board that is going away (THE RULE) —
      and forget a trigger parked there, which has nowhere to go back to. */
  #detachIntegrationTags(boardId) {
    for (const e of this.#doc.integrations) {
      if (parseAddress(e.parkedTrigger?.anchor)?.boardId === boardId) {
        delete e.parkedTrigger;
      }
      if (!e.tags) continue;
      for (const [key, tag] of Object.entries(e.tags)) {
        if (parseAddress(tag.anchor)?.boardId !== boardId) continue;
        delete e.tags[key];
        releaseDataPin(e, key);
      }
      if (!Object.keys(e.tags).length) delete e.tags;
    }
  }

  // ── Scope channels: the logic-analyzer instrument setup (Feature 210) ──────
  // An ordered list of channel bindings persisted with the design so a saved
  // schematic keeps its analyzer setup. A `net` channel binds to a member
  // ADDRESS (surviving a re-key like a net name); a `bus` channel binds to a
  // bus id. Passive — no occupancy/netlist/engine effect; a dead ref reads as
  // undriven, coming back to life if its target returns (undo, re-add).

  /** How many net names the desk holds (no copies made). */
  get netNameCount() {
    return this.#doc.netNames.length;
  }

  /** The analyzer's ground lead — a member address — or null (each voltage
      lane read against 0 V). */
  get scopeGround() {
    return this.#doc.scopeGround ?? null;
  }

  /** Put the analyzer's ground lead on `address`, or take it off (null).
      Returns whether anything changed. Throws INVALID_ARG for an address
      that does not parse. */
  setScopeGround(address) {
    if (address == null || address === "") {
      if (this.#doc.scopeGround == null) return false;
      delete this.#doc.scopeGround;
      return true;
    }
    if (typeof address !== "string" || !parseAddress(address)) {
      throw taggedError(`not an address: ${address}`, "INVALID_ARG");
    }
    if (this.#doc.scopeGround === address) return false;
    this.#doc.scopeGround = address;
    return true;
  }

  /** Copies of the analyzer channels, in display order. */
  get scopeChannels() {
    return this.#doc.scopeChannels.map((c) => ({ ...c }));
  }

  /** A copy of one channel, or null. */
  getScopeChannel(id) {
    const c = this.#doc.scopeChannels.find((x) => x.id === id);
    return c ? { ...c } : null;
  }

  /** True if a channel already tracks this (kind, ref) — avoids duplicate lanes. */
  hasScopeChannel(kind, ref) {
    return this.#doc.scopeChannels.some(
      (c) => c.kind === kind && c.ref === ref,
    );
  }

  /**
   * Append a channel bound to a net address or a bus id. `extra` may carry
   * `label` and `color`. Throws INVALID_KIND / INVALID_ARG. Returns a copy.
   */
  addScopeChannel(kind, ref, { color = null, label = null } = {}) {
    if (!SCOPE_CHANNEL_KINDS.has(kind)) {
      throw taggedError(
        `unsupported scope channel kind: ${kind}`,
        "INVALID_KIND",
      );
    }
    if (typeof ref !== "string" || !ref) {
      throw taggedError(
        "scope channel ref must be a non-empty string",
        "INVALID_ARG",
      );
    }
    const ch = { id: `sc${this.#doc.nextScopeChannelId++}`, kind, ref };
    if (label) ch.label = label;
    if (color) ch.color = color;
    this.#doc.scopeChannels.push(ch);
    return { ...ch };
  }

  /**
   * Patch a channel's `label` or `color` (a null/empty value clears it). Throws
   * NOT_FOUND. Returns a copy.
   */
  updateScopeChannel(id, patch = {}) {
    const ch = this.#doc.scopeChannels.find((c) => c.id === id);
    if (!ch) throw taggedError(`no scope channel ${id}`, "NOT_FOUND");
    if ("label" in patch) {
      if (patch.label) ch.label = patch.label;
      else delete ch.label;
    }
    if ("color" in patch) {
      if (patch.color) ch.color = patch.color;
      else delete ch.color;
    }
    return { ...ch };
  }

  /** Remove a channel. Throws NOT_FOUND. */
  removeScopeChannel(id) {
    const i = this.#doc.scopeChannels.findIndex((c) => c.id === id);
    if (i === -1) throw taggedError(`no scope channel ${id}`, "NOT_FOUND");
    this.#doc.scopeChannels.splice(i, 1);
  }

  /** Reorder a channel to a new (clamped) index. Throws NOT_FOUND. */
  moveScopeChannel(id, index) {
    const from = this.#doc.scopeChannels.findIndex((c) => c.id === id);
    if (from === -1) throw taggedError(`no scope channel ${id}`, "NOT_FOUND");
    const to = Math.max(
      0,
      Math.min(this.#doc.scopeChannels.length - 1, Math.floor(index) || 0),
    );
    const [ch] = this.#doc.scopeChannels.splice(from, 1);
    this.#doc.scopeChannels.splice(to, 0, ch);
  }

  // ── Design paste (Feature 240) ───────────────────────────────────────────

  /**
   * Stamp a captured design (model/design-clip.js) onto this desk, translated
   * by an integer `shift`: its boards, the parts seated on them, the desk
   * bricks that travelled with it, all the wiring between them, and the bus /
   * net-name / anchored-label metadata riding those. Everything arrives with
   * FRESH ids from this document's own counters — a paste is new hardware, not
   * a reference to the design it came from.
   *
   * ALL-OR-NOTHING, and that is the whole reason it lives here rather than in
   * the controller: half a design is not a design (a board left behind silently
   * cuts every wire that crossed to it). The document is snapshotted first, and
   * ANY failure — an overlapping board, an occupied hole — restores it and
   * rethrows, so a refused paste leaves the desk exactly as it was.
   *
   * Mating is NOT applied here: the caller offers the new strips to
   * `joinMatedGroup` afterwards, exactly as it does for a placed kit, so a
   * design dropped flush against an existing board joins its group.
   *
   * @param {object} clip - from captureDesign
   * @param {{dx:number, dy:number}} shift
   * @returns {{boards:Array, components:Array, wires:Array, buses:Array,
   *   annotations:Array}} copies of everything created.
   */
  pasteDesign(clip, shift = { dx: 0, dy: 0 }) {
    const before = this.snapshot();
    // x is the column lattice; y is the 0.01 grid a board is stored on. The
    // ghost's magnetic pull lands a strip flush against one at a FRACTIONAL y
    // (a kit's bottom rail ends at 21.02) and judges the drop there — rounding
    // it here committed a different position from the one shown green, which
    // either overlapped (a refused paste) or left a gap that never mated.
    const dx = Math.round(shift.dx);
    const dy = boardCoord(shift.dy);
    try {
      // Clip key → the id it landed under here. Boards and bricks share one
      // map because a wire endpoint's owner may be either.
      const owners = new Map();
      const groups = new Map(); // clip group → a fresh group id
      const boards = [];
      for (const b of clip.boards) {
        const added = this.addBoard(b.type, b.x + dx, b.y + dy, b.rot);
        if (b.cuts?.length) this.setCuts(added.id, b.cuts);
        // addBoard named it after its NEW id. A name that was still the
        // source's own id is that same default, not the user's words — copied,
        // `bb7` would arrive calling itself `bb2`, the board it came from.
        const name = b.name && b.name !== b.key ? b.name : undefined;
        if (name || b.description) {
          this.setBoardParams(added.id, {
            name, // undefined leaves addBoard's alone
            description: b.description ?? "",
          });
        }
        if (b.group != null) {
          if (!groups.has(b.group)) {
            groups.set(b.group, `g${this.#doc.nextGroupId++}`);
          }
          this.#doc.boards.find((x) => x.id === added.id).group = groups.get(b.group); // prettier-ignore
        }
        owners.set(b.key, added.id);
        boards.push(this.getBoard(added.id));
      }
      const components = [];
      for (const b of clip.bricks) {
        const brick = this.addBrick(b.kind, b.x + dx, b.y + dy, b.params);
        owners.set(b.key, brick.id);
        components.push(brick);
      }
      // One occupancy index for every part and wire the paste lays, kept in
      // step as each lands (addComponent / addWire) — the desk's own claims
      // plus the paste's so far, exactly what a rebuild per item would read.
      this.#batchOcc = buildOccupancy(this.#doc);
      const parts = new Map(); // clip key → new component id (label anchors)
      for (const p of clip.parts) {
        const boardId = owners.get(p.board);
        if (!boardId) continue; // a part whose board wasn't captured
        const comp = this.addComponent({
          kind: p.kind,
          ref: p.ref,
          board: boardId,
          anchor: p.anchor,
          params: p.params,
        });
        if (p.name || p.description) {
          this.setComponentMeta(comp.id, {
            name: p.name ?? "",
            description: p.description ?? "",
          });
        }
        parts.set(p.key, comp.id);
        components.push(
          p.name || p.description ? this.getComponent(comp.id) : comp,
        );
      }
      const wireIds = new Map(); // clip key → new wire id (bus members)
      const wires = [];
      for (const w of clip.wires) {
        const from = owners.get(w.from.owner);
        const to = owners.get(w.to.owner);
        if (!from || !to) continue;
        const wire = this.addWire({
          from: formatAddress(from, w.from.point),
          to: formatAddress(to, w.to.point),
          color: w.color,
          layout: w.layout ?? "direct",
          // Waypoints are desk coordinates, so unlike the addresses either
          // side of them they have to ride the paste shift themselves.
          points: (w.points ?? []).map((p) => ({ x: p.x + dx, y: p.y + dy })),
        });
        if (w.name || w.description) {
          this.setWireMeta(wire.id, {
            name: w.name ?? "",
            description: w.description ?? "",
          });
        }
        wireIds.set(w.key, wire.id);
        wires.push(w.name || w.description ? this.getWire(wire.id) : wire);
      }
      this.#batchOcc = null; // the rest (flags, tags) checks the live desk
      const buses = (clip.buses ?? []).map((b) =>
        this.addBus(
          b.name,
          b.members.map((m) => wireIds.get(m)).filter(Boolean),
          { color: b.color },
        ),
      );
      for (const n of clip.netNames ?? []) {
        const owner = owners.get(n.owner);
        if (owner) this.nameNet(formatAddress(owner, n.point), n.name);
      }
      const annotations = [];
      for (const a of clip.annotations ?? []) {
        const anchor = parts.get(a.anchor);
        if (!anchor) continue;
        annotations.push(
          this.addAnnotation(a.kind, a.x + dx, a.y + dy, a.text, {
            color: a.color ?? null,
            anchor,
          }),
        );
      }
      // Signals are the ONE part of a clip that is best-effort rather than
      // all-or-nothing, and the argument is what all-or-nothing exists FOR:
      // half a design silently cuts the wires that crossed to the board left
      // behind — and a signal cuts nothing. Refusing a whole sub-assembly
      // because this desk already holds every stimulus button it can would
      // make the cap punish the wrong thing. At the cap → the signal is
      // DROPPED; its hole already taken → it arrives UNPLACED (the loader's
      // own rule: a flag with nowhere to go stops existing, the signal never
      // does).
      // `droppedSignals` is reported, never swallowed.
      const signals = [];
      let droppedSignals = 0;
      for (const sig of clip.signals ?? []) {
        const owner = owners.get(sig.flag.owner);
        if (!owner) {
          droppedSignals++;
          continue;
        }
        if (this.#doc.signals.length >= MAX_SIGNALS) {
          droppedSignals++;
          continue;
        }
        const added = this.addSignal({ type: sig.type, rest: sig.rest });
        if (sig.name || sig.description) {
          this.setSignalMeta(added.id, {
            name: sig.name ?? "",
            description: sig.description ?? "",
          });
        }
        const address = formatAddress(owner, sig.flag.point);
        if (this.canPlaceSignalFlag(added.id, address)) {
          this.plantSignalFlag(added.id, address, sig.flag.rot ?? 0);
        }
        signals.push(this.getSignal(added.id));
      }
      return {
        boards,
        components,
        wires,
        buses,
        annotations,
        signals,
        droppedSignals,
      };
    } catch (err) {
      this.#batchOcc = null;
      this.restore(before); // a refused paste changes nothing at all
      throw err;
    }
  }

  // ── Whole-desk translation ───────────────────────────────────────────────

  /**
   * Slide the ENTIRE desk by (dx, dy) — a whole pitch across, the 0.01 grid
   * down (see below): every board, every desk-level
   * brick, and every annotation. Seated parts and wires need nothing — they are
   * stored as board addresses, not coordinates, so they ride their board. A
   * signal FLAG is an address too, so it is absent here for the same reason and
   * not by oversight — worth saying, since the two things that DO need carrying
   * (an annotation's free coordinates, a routed wire's waypoints) make the
   * omission look like one.
   *
   * The move is RIGID, which is why there is no legality check and no way to
   * refuse it: nothing changes its position relative to anything else, so
   * nothing that was clear can start overlapping and nothing that was mated can
   * come apart. It exists so a design that has wandered far from the origin can
   * be pulled back around it (the fit-to-screen recentre) rather than drifting
   * ever further out into the coordinate range.
   *
   * @returns {{dx:number, dy:number}} the rounded delta actually applied.
   */
  translateAll(dx, dy) {
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
      throw taggedError("desk delta must be finite", "INVALID_ARG");
    }
    // x rounds to the column lattice; y takes the same 0.01 grid a board is
    // stored on, there being no vertical lattice to round to any more. A
    // recentre that rounded y would leave the desk up to half a pitch off the
    // origin — visible on a fit, and enough to leave `make demos` reporting an
    // uncentred bench.
    const [ix, iy] = [Math.round(dx), boardCoord(dy)];
    if (ix === 0 && iy === 0) return { dx: 0, dy: 0 };
    for (const board of this.#doc.boards) {
      // Quantized, not accumulated: a board's y is fractional now (a kit's
      // lower strips sit at 3.50 and 17.52), and `+=` down a session of
      // recentres would walk it off the grid one ulp at a time until a
      // dovetail stopped being flush.
      board.x = boardCoord(board.x + ix);
      board.y = boardCoord(board.y + iy);
    }
    // Everything below is quantized like the boards and for the same reason:
    // `+=` down a session of recentres leaves -17.009999999999998 behind.
    for (const comp of this.#doc.components) {
      // Bricks alone carry desk coordinates; a seated part has board + anchor.
      if (comp.board == null && Number.isFinite(comp.x)) {
        comp.x += ix;
        comp.y = boardCoord(comp.y + iy);
      }
    }
    // A label's position is absolute even when it is anchored to a part (the
    // anchor only makes it ride that part's drag), so every one moves.
    for (const ann of this.#doc.annotations) {
      ann.x += ix;
      ann.y = boardCoord(ann.y + iy);
    }
    // A routed wire's waypoints are the one part of a wire that is NOT an
    // address, so they are the one part that has to be moved by hand — leave
    // them and a design slid to the origin would drag its routing behind it.
    for (const wire of this.#doc.wires) {
      for (const p of wire.points ?? []) {
        p.x = wireCoord(p.x + ix);
        p.y = wireCoord(p.y + iy);
      }
    }
    return { dx: ix, dy: iy };
  }

  /** The serializable document (a deep copy — safe to hand to IPC). */
  toJSON() {
    return structuredClone(this.#doc);
  }

  /**
   * An immutable snapshot of the whole document for the undo/redo history
   * (Feature 200) — a deep copy, so later mutations never bleed into it. Same
   * shape as toJSON; named for its role as a history entry.
   */
  snapshot() {
    return structuredClone(this.#doc);
  }

  /**
   * Replace the whole document with a `snapshot` (an undo/redo restore). The
   * snapshot is deep-copied in, so the caller may keep re-restoring the same
   * one. It is trusted to be a valid document (it came from snapshot()/toJSON),
   * so it is NOT re-normalized — restore is byte-exact, the round-trip
   * undo/redo relies on.
   */
  restore(snapshot) {
    this.#doc = structuredClone(snapshot);
    this.#noteProbes();
  }

  /**
   * Replace the whole document with one loaded from OUTSIDE — a file, or
   * another project tab (Feature 240). Unlike `restore`, the input is
   * untrusted, so it goes through the same normalization the constructor
   * applies: a hand-edited or older file can never leave a half-valid document
   * on the desk. The counterpart to `restore` (trusted, byte-exact) — pick by
   * where the document came from, never by convenience.
   */
  load(raw) {
    this.#doc = normalizeDocument(raw);
    this.#noteProbes();
  }

  /**
   * A count that moves whenever the document may have changed: every public
   * method but `DESK_DOC_READS` bumps it as it is called (an edit, a
   * `restore`, a `load` — and a refused edit too, which is only a wasted
   * check). Equal revisions on the one DeskDoc mean the same document, which
   * is what lets the auto-save tick skip its signature (ProjectWorkspace
   * `#signature`) — a tick that cannot listen for `doc-changed` (see there).
   */
  get revision() {
    return this.#revision;
  }

  // Each changing method, wrapped to bump the revision BEFORE it runs, so
  // one that throws part-way still counts.
  static {
    const proto = DeskDoc.prototype;
    for (const name of Object.getOwnPropertyNames(proto)) {
      if (name === "constructor" || DESK_DOC_READS.has(name)) continue;
      const desc = Object.getOwnPropertyDescriptor(proto, name);
      if (typeof desc.value !== "function") continue;
      const method = desc.value;
      Object.defineProperty(proto, name, {
        ...desc,
        value: {
          [name](...args) {
            this.#revision += 1;
            return method.apply(this, args);
          },
        }[name],
      });
    }
  }
}
